/** Luxon from jsDelivr ESM — no npm install or build step. If CSP blocks CDN, host luxon in /assets and change this URL. */
import { DateTime, Settings } from 'https://cdn.jsdelivr.net/npm/luxon@3.5.0/+esm';
import md5 from 'https://esm.sh/blueimp-md5@2.19.0';

(function() {
    const ORDER_STATUS_DESPATCHED = window.orderTrackerTranslations?.statusDespatched;
    const ORDER_STATUS_PREPARING_FOR_DESPATCH = window.orderTrackerTranslations?.statusPreparingForDespatch;
    const ORDER_STATUS_OUTSTANDING_ITEMS = window.orderTrackerTranslations?.statusOutstandingProducts;
    const ORDER_STATUS_CANCELLED_ITEMS = window.orderTrackerTranslations?.statusCancelledProducts;

    const queryString = window.location.search;
    const urlParameters = new URLSearchParams(queryString);
    // SECURITY FIX: Get API key from metafield instead of hardcoding
    const apiKeyContainer = document.querySelector('#apiKeyContainer');
    const clientApiKey = apiKeyContainer?.dataset.apiKey;

    /** Parsed API dates = calendar-only, stored as UTC start-of-day. UK locale + London for “today” thresholds. */
    const ORDER_TRACKER_LOCALE = 'en-GB';
    Settings.defaultLocale = ORDER_TRACKER_LOCALE; // avoids repeating .setLocale() on every DateTime
    const ORDER_TRACKER_BUSINESS_TZ = 'Europe/London';

    const FORMAT_DATE_MONTH_NAMES = window.orderTrackerTranslations.monthNamesFull;
    const FORMAT_DATE_MONTH_NAMES_SHORT = window.orderTrackerTranslations.monthNamesShort;

    const selectors = {
        formContainer: '#form_container',
        alertContainer: '#alert_container',
        orderDetailsContainer: '#order_details_container',
        orderLayoutContainer: '.order-layout__shipments',
        orderIdInput: '#order_id',
        billingEmailInput: '#billingEmail',
        trackButton: '#trackorder_button',
        loadingMessage: '#loading-message',
        originalDeliveryDate: '#original_delivery_date',
    };

    /** Last successful order payload; used for order-amends flow lookups. */
    let cachedOrderData = null;

    document.addEventListener('orderTrackerAddressUpdateSuccess', (ev) => {
        const addr = ev.detail?.shippingAddress;
        const fulfillmentDocumentNo = ev.detail?.fulfillmentDocumentNo || null;
        if (!addr || !cachedOrderData?.orderDetails) return;

        if (fulfillmentDocumentNo) {
            updateEditableShipmentAddress(cachedOrderData, addr, fulfillmentDocumentNo);
        } else {
            cachedOrderData.orderDetails.shippingAddress = {
                ...(cachedOrderData.orderDetails.shippingAddress || {}),
                ...addr,
            };
            syncEditableShipmentAddresses(cachedOrderData, addr);
        }

        populateCustomerInfo(cachedOrderData);
        renderShipmentCards(cachedOrderData);
    });

    function getOrderTrackerDrawer() {
        return document.querySelector('order-tracker-drawer');
    }

    function closeOrderTrackerDrawer() {
        getOrderTrackerDrawer()?.close?.();
    }

    function replaceSmartTags(text, replacements) {
        return text.replace(/{{(.*?)}}/g, (match, p1) => {
            const key = p1.trim();
            return key.split('.').reduce((o, k) => o?.[k], replacements) ?? '';
        });
    }

    /** Encode text for safe insertion into HTML text nodes or PCDATA. */
    function escapeHtml(value) {
        if (value == null) return '';
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    /** Encode for double-quoted HTML attribute values. */
    function escapeHtmlAttr(value) {
        return escapeHtml(value);
    }

    /** Block dangerous URL schemes when interpolating into href or src. */
    function sanitizeHref(url) {
        if (url == null) return '';
        const s = String(url).trim();
        if (!s) return '';
        const lower = s.toLowerCase();
        if (lower.startsWith('javascript:') || lower.startsWith('vbscript:') || lower.startsWith('data:')) {
            return '';
        }
        return s;
    }

    function getOrderTrackerTimelineConfig() {
        const el = document.getElementById('order-tracking');
        const proximity = parseInt(el?.dataset.timelineProximityDays ?? 5, 10);
        const maxExplicit = parseInt(el?.dataset.timelineMaxExplicitGroups ?? 2, 10);

        return {
            proximityDays: Number.isFinite(proximity) ? Math.max(0, proximity) : 5,
            maxExplicitGroups: Number.isFinite(maxExplicit) ? Math.max(1, Math.min(5, maxExplicit)) : 2,
        };
    }

    function notifyAddressUpdateOrderDisplayed(orderData) {
        try {
            window.OrderTrackerAddressUpdate?.onOrderDisplayed?.(orderData);
        } catch (err) {
            console.error('Address update onOrderDisplayed:', err);
        }
    }

    function notifyAddressUpdateOrderHidden() {
        try {
            window.OrderTrackerAddressUpdate?.onOrderHidden?.();
        } catch (err) {
            console.error('Address update on OrderHidden:', err);
        }
    }

    function utcFromJsDate(d) {
        return DateTime.fromJSDate(d, { zone: 'utc' });
    }

    /** YYYY-MM-DD in Europe/London for the given instant (default: now) */
    function getUKBusinessCalendarKey(d = new Date()) {
        return DateTime.fromJSDate(d).setZone(ORDER_TRACKER_BUSINESS_TZ).toFormat('yyyy-LL-dd');
    }

    /** YYYY-MM-DD for UTC calendar date (matches parseDateFromDMY / API d/m/y) */
    function getUTCCalendarKey(d) {
        return utcFromJsDate(d).toFormat('yyyy-LL-dd');
    }

    function calendarKeysDiffDays(laterKey, earlierKey) {
        const parseKey = (key) => {
            const [y, m, d] = key.split('-').map((n) => parseInt(n, 10));
            return DateTime.utc(y, m, d).startOf('day');
        };
        return Math.floor(parseKey(laterKey).diff(parseKey(earlierKey), 'days').days);
    }

    /** Absolute calendar-day distance between two JS Dates (UTC calendar keys). */
    function calendarDaysApart(dateA, dateB) {
        if (!dateA || !dateB) return Infinity;
        const ka = getUTCCalendarKey(dateA);
        const kb = getUTCCalendarKey(dateB);
        return ka <= kb ? calendarKeysDiffDays(kb, ka) : calendarKeysDiffDays(ka, kb);
    }

    function getTrackButton() {
        return document.getElementById('trackorder_button');
    }

    function setTrackButtonState(state = 'idle') {
        const trackButton = getTrackButton();
        if (!trackButton) return;

        if (state === 'loading') {
            if (!trackButton.hasAttribute('data-original-text')) {
                trackButton.setAttribute('data-original-text', trackButton.innerHTML);
            }
            trackButton.innerHTML = `<div class="loading-spinner"><div class="spinner"></div>${escapeHtml(window.orderTrackerTranslations?.loadingText)}</div>`;
            trackButton.classList.add('disabled');
            return;
        }

        if (state === 'disabled') {
            trackButton.classList.add('disabled');
            return;
        }

        // idle
        const originalText = trackButton.getAttribute('data-original-text');
        if (originalText) {
            trackButton.innerHTML = originalText;
            trackButton.removeAttribute('data-original-text');
        }
        trackButton.classList.remove('disabled');
    }

    function handleTrackButtonClick(e) {
        e.preventDefault();
        setTrackButtonState('loading');

        const orderID = document.getElementById('order_id').value;
        const billingEmail = document.getElementById('billingEmail').value;

        if (!orderID || !billingEmail) {
            alert(window.orderTrackerTranslations?.errorFieldsRequired);
            setTrackButtonState('idle');
            return;
        }

        if (!clientApiKey) {
            alert(window.orderTrackerTranslations?.errorConfigError);
            setTrackButtonState('idle');
            return;
        }

        fetchOrderDetails(clientApiKey, billingEmail, orderID);
    }

    // Event listeners
    document.addEventListener('DOMContentLoaded', function() {
        window.OrderAmendsFlow?.setFormatMoney?.(formatMoney);
        window.OrderAmendsFlow?.setStatusConstants?.(
            ORDER_STATUS_PREPARING_FOR_DESPATCH,
            ORDER_STATUS_OUTSTANDING_ITEMS
        );
        window.OrderAmendsFlow?.setRefetchOrderHandler?.(() => {
            const clientApiKey = document.querySelector('#apiKeyContainer')?.dataset.apiKey;
            const orderID = document.getElementById('order_id')?.value;
            const billingEmail = document.getElementById('billingEmail')?.value;
            if (clientApiKey && orderID && billingEmail) {
                fetchOrderDetails(clientApiKey, billingEmail, orderID);
            }
        });
        window.OrderAmendsFlow?.initOrderAmends?.();
        window.OrderReturnsFlow?.initOrderReturns?.();
        window.OrderReportIssueFlow?.initOrderReportIssue?.();

        // Track order button click
        const trackButton = getTrackButton();
        if (trackButton) {
            trackButton.addEventListener('click', handleTrackButtonClick);
        }

        // Track another order buttons click (both alert and order details)
        const trackAnotherButtons = document.querySelectorAll('.track-another-order-btn');
        trackAnotherButtons.forEach(button => {
            button.addEventListener('click', function(e) {
                e.preventDefault();
                showForm();
            });
        });

        // Check for URL parameters (support both 'order' and 'orderNumber')
        const billingEmail = urlParameters.get('email');
        const orderID = urlParameters.get('order') || urlParameters.get('orderNumber');

        if( orderID && billingEmail ) {
            // Clean and validate URL parameters
            const cleanOrder = orderID.toString().trim().replace(/[^a-zA-Z0-9]/g, '');
            const cleanEmail = billingEmail.toString().trim().toLowerCase();
            
            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            
            if (cleanOrder && emailRegex.test(cleanEmail)) {
                // Pre-fill form fields with URL parameters
                document.getElementById('order_id').value = cleanOrder;
                document.getElementById('billingEmail').value = cleanEmail;
                
                // Hide form and fetch order details automatically
                toggleElementVisibility(selectors.formContainer, false);
                setTrackButtonState('disabled');

                fetchOrderDetails( clientApiKey, cleanEmail, cleanOrder );
            } else {
                // Invalid parameters, show error
                showError(window.orderTrackerTranslations?.errorInvalidUrl);
            }
        } else {
            showForm();
        }
    });

    function restoreTrackButtonAfterTrackRequest() {
        setTrackButtonState('idle');
    }

    function fetchOrderDetails ( clientApiKey, email, orderNumber) {
        showLoadingScreen();

        fetch(`https://app-lj-shipping-charges.azurewebsites.net/ShopifyOrderStatus/order-status-details?clientApiKey=${clientApiKey}&orderNumber=${encodeURIComponent(orderNumber)}&customerEmail=${encodeURIComponent(email)}`)
            .then(res => {
                if (!res.ok) {
                    // Handle HTTP error status codes (400, 500, etc.)
                    throw new Error(`HTTP ${res.status}: ${res.statusText}`);
                }

                return res.json();
            })
            .then(responseData => {
                restoreTrackButtonAfterTrackRequest();

                if (responseData && (responseData.customerInformation || responseData.orderDetail)) {
                    if (responseData.orderDetail.orderItems) {
                        // Update URL with current search parameters
                        const newUrl = `${window.location.pathname}?order=${encodeURIComponent(orderNumber)}&email=${encodeURIComponent(email)}`;
                        history.pushState({ order: orderNumber, email: email }, '', newUrl);
                        // Set currency based on the API response
                        updateMoneyCurrency(responseData);

                        // Map real API response to expected format
                        const orderData = mapApiResponseToExpectedFormat(responseData);

                        displayOrderDetails(orderData);
                    } else {
                         showError(window.orderTrackerTranslations?.errorOrderDetailsNotSynced);
                    }
                } else {
                    showError(responseData.errorMessage || window.orderTrackerTranslations?.errorOrderNotFound);
                }

                toggleElementVisibility(selectors.loadingMessage, false);
            })
            .catch(err => {
                restoreTrackButtonAfterTrackRequest();
                console.error('API Error:', err);

                // Show generic error message while keeping form visible and input values
                showError(window.orderTrackerTranslations?.errorApiError);
            });
    }

    function updateMoneyCurrency(apiData) {
        if (apiData.orderSummary?.currencyDisplaySymbol) {
            // Replace only the currency symbol in window.moneyFormat, retaining everything after the symbol (e.g., ${{amount}} -> £{{amount}})
            if (window.moneyFormat && typeof apiData.orderSummary?.currencyDisplaySymbol === 'string') {
                window.moneyFormat = window.moneyFormat.replace(/^(\D*)(\{\{amount\}\})/, apiData.orderSummary.currencyDisplaySymbol + '$2');
            }
        }
    }

    // Function to map real API response to expected format
    function mapApiResponseToExpectedFormat(apiData) {
        const customerInformation = {
            firstName: String(apiData.customerInformation?.firstName ?? ''),
            lastName: String(apiData.customerInformation?.lastName ?? ''),
            fullName: String(apiData.customerInformation?.fullName ?? ''),
            email: String(apiData.customerInformation?.email ?? ''),
            phone: apiData.customerInformation?.phone ?? null,
        };
    
        const rawSummary = apiData.orderSummary ?? {};
    
        const orderSummary = {
            orderDate: String(rawSummary.orderDate ?? ''),
            orderNo: String(rawSummary.orderNo ?? ''),
            orderInternalId: String(rawSummary.orderInternalId ?? ''),
            shopifyOrderInternalId: rawSummary.shopifyOrderInternalId ?? null,
            estimatedDeliveryDate: parseDateFromDMY(rawSummary.estimatedDeliveryDate ?? ''),
            subtotal: parseNumber(rawSummary.subTotal),
            shippingTotal: parseNumber(rawSummary.shipping),
            discountTotal: parseNumber(rawSummary.discounts),
            taxTotal: parseNumber(rawSummary.tax),
            transactionTotal: parseNumber(rawSummary.total),
            giftCardTotal: getGiftCardTotalUsed(rawSummary),
            currencySymbol: String(rawSummary.currencySymbol ?? ''),
            refundTotals:  Array.isArray(rawSummary.refunds) ? rawSummary.refunds : [],
    
            // Objects — null when absent, not empty strings
            paymentMethod:  Array.isArray(rawSummary.paymentMethod)
                                ? rawSummary.paymentMethod
                                : null,
            shippingMethod: rawSummary.shippingMethod
                                ? {
                                    name: String(rawSummary.shippingMethod.name ?? ''),
                                    code: String(rawSummary.shippingMethod.code ?? ''),
                                    carrierIdentifier: String(rawSummary.shippingMethod.carrierIdentifier ?? ''),
                                  }
                                : null,
        };
    
        // ─── Order Details ───────────────────────────────────────────────────────
        const rawDetail = apiData.orderDetail ?? {};

        const orderDetails = mapOrderDetails(rawDetail?.orderItems, rawDetail?.shippingAddress);

        return { customerInformation, orderSummary, orderDetails };
    }

    function mapOrderDetails(orderItems, shippingAddress = null) {
        const cancelledItems = orderItems?.cancelledItems || [];
        const shipments = sortShipmentBatchesByEarliestDate(
            (orderItems?.fulfilledShipments ?? []).map(s =>
                mapShipmentOrBatch(s, ORDER_STATUS_DESPATCHED)
            )
        );
        const preparingForDespatchShipments = sortShipmentBatchesByEarliestDate(
            (orderItems?.shipmentInProgressItems ?? []).map(s =>
                mapShipmentOrBatch(s, ORDER_STATUS_PREPARING_FOR_DESPATCH)
            )
        );
        const outstandingBatches = sortShipmentBatchesByEarliestDate(
            (orderItems?.outstandingItems ?? []).map(s =>
                mapShipmentOrBatch(s, ORDER_STATUS_OUTSTANDING_ITEMS)
            )
        );
        const shipmentSequence = [
            ...shipments.map(batch => ({ status: ORDER_STATUS_DESPATCHED, batch })),
            ...preparingForDespatchShipments.map(batch => ({ status: ORDER_STATUS_PREPARING_FOR_DESPATCH, batch })),
            ...outstandingBatches.map(batch => ({ status: ORDER_STATUS_OUTSTANDING_ITEMS, batch })),
        ].sort((a, b) => getEarliestShipmentOrBatchSortTime(a.batch) - getEarliestShipmentOrBatchSortTime(b.batch));

        return {
            shippingAddress: mapShippingAddress(shippingAddress),
            orderItems: {
                shipments,
                preparingForDespatchShipments,
                outstandingBatches,
                shipmentSequence,
    
                cancelledItems: cancelledItems.length > 0 ? [{
                    items: cancelledItems,
                    status: ORDER_STATUS_CANCELLED_ITEMS,
                    shippingAddress: mapShippingAddress(shippingAddress),
                }].map(s => mapShipmentOrBatch(s, ORDER_STATUS_CANCELLED_ITEMS)) : [],
            },
        }
    }

    /** Always returns a finite number; NaN / null / undefined → 0 */
    function parseNumber(value) {
        const n = parseFloat(value);
        return isFinite(n) ? n : 0;
    }

    function formatMoney(val) {
        var format = window.moneyFormat;

        const num = parseFloat(val);
        const safe = Number.isFinite(num) ? num : 0;
        const lang =
            (typeof document !== 'undefined' && document.documentElement.lang) ||
            'en-GB';
        const amount = safe.toLocaleString(lang, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        var regexp = /^([^{}]*)\{\{amount\}\}([^{}]*)$/;
        if (format && format.match(regexp)) {
            return format.replace(regexp, '$1' + amount + '$2');
        }

        return amount;
    }
    
    function mapShippingAddress(raw) {
        if (!raw) return null;
        return {
            addressee: String(raw.addressee ?? ''),
            address1: String(raw.address1 ?? ''),
            address2: String(raw.address2 ?? ''),
            attention: raw.attention ?? null,
            city: String(raw.city ?? ''),
            state: String(raw.state ?? ''),
            zipCode: String(raw.zipCode ?? ''),
            country: String(raw.country ?? ''),
            shippingPhone: raw.shippingPhone ?? null,
        };
    }
    
    function mapShipmentOrBatch(raw, status) {
        const trackingInfo = raw.trackingInformation ?? raw.trackingInfo ?? null;
        
        const shipmentDelivery = {
            delayedChangeReasonDescription: raw.delayedChangeReasonDescription ?? '',
            earliestDeliveryDate: parseDateFromDMY(raw.earliestDeliveryDate ?? null),
            latestDeliveryDate: parseDateFromDMY(raw.latestDeliveryDate ?? null),
            revisedEarliestDeliveryDate: parseDateFromDMY(raw.revisedEarliestDeliveryDate ?? null),
            revisedLatestDeliveryDate: parseDateFromDMY(raw.revisedLatestDeliveryDate ?? null),
        };
        shipmentDelivery.hasOriginalDeliveryDates = shipmentDelivery.earliestDeliveryDate != null && shipmentDelivery.latestDeliveryDate != null; 
        shipmentDelivery.hasRevisedDeliveryDates = shipmentDelivery.revisedEarliestDeliveryDate != null && shipmentDelivery.revisedLatestDeliveryDate != null;

        return {
            status,
            documentNo: String(raw.fulfillmentDocumentNo ?? raw.documentNo ?? ''),
            fulfillmentId:
              raw.fulfillmentId != null
                ? String(raw.fulfillmentId)
                : raw.fulfillmentID != null
                  ? String(raw.fulfillmentID)
                  : null,
            date: parseDateFromDMY(raw.fulfilledDate ?? raw.date ?? ''),
            shippingAddress: mapShippingAddress(raw.shippingAddress),
            trackingInfo,
            ...shipmentDelivery,
            items: (raw.items ?? []).map(item => mapItemData(item, { trackingInfo })),
        };
    }

    /** Earliest meaningful timestamp for ordering shipment/batch rows (fulfilment date + delivery window fields). */
    function getEarliestShipmentOrBatchSortTime(batch) {
        if (!batch) return Number.POSITIVE_INFINITY;

        const { earliestDate } = getEarliestAndLatestDate(batch);

        return earliestDate.getTime();
    }

    function sortShipmentBatchesByEarliestDate(batches) {
        if (!Array.isArray(batches) || batches.length <= 1) return batches;
        return [...batches].sort((a, b) => getEarliestShipmentOrBatchSortTime(a) - getEarliestShipmentOrBatchSortTime(b));
    }

    /**
     * One entry from replacement.orders[] — same shape as main API but only these summary fields are kept.
     * orderItems buckets use the same shipment mapping as the primary order.
     */
    function mapReplacementOrderEntry(orderRaw) {
        const rawSummary = orderRaw?.orderSummary ?? {};
        const rawDetail = orderRaw?.orderDetail ?? {};
        const rawItems = rawDetail.orderItems ?? {};

        return {
            orderSummary: {
                orderDate: String(rawSummary.orderDate ?? ''),
                orderNo: String(rawSummary.orderNo ?? ''),
                estimatedDeliveryDate: parseDateFromDMY(rawSummary.estimatedDeliveryDate ?? ''),
                faultType: rawSummary.faultType != null ? String(rawSummary.faultType) : null,
            },
            orderDetails: mapOrderDetails(rawItems),
        };
    }

    function mapItemData(item, context = null) {
        const ret = (item.returns ?? [])[0] ?? null;

        return {
            id: item.id ?? null,
            itemType: String(item.itemType ?? ''),
            itemNumber: String(item.itemNumber ?? ''),
            itemDescription: String(item.itemDescription ?? ''),
            externalSystem: item.externalSystem ?? null,
            reportingCode: item.reportingCode ?? null,

            setItem: item.setItem ?? null,
            setQuantity: item.setQuantity != null ? parseInt(item.setQuantity, 10) : null,
            setItemReportingCode: item.setItemReportingCode ?? null,

            itemImageUrl: item.itemImage ? String(item.itemImage) : window.placeholderImage,
            quantity: parseInt(item.quantity, 10) || 0,
            grossAmount: item.grossAmount ?? null,
            unitPrice: item.unitPrice,

            assemblyInstructionRequired: Boolean(item.assemblyInstructionRequired),
            assemblyInstructionUrl: item.assemblyInstructionUrl ?? null,
    
            returnReason: ret?.solution ?? ret?.returnReason ?? null,
            returnQuantity: ret ? (parseInt(ret.quantity ?? ret.returnQuantity, 10) || 0) : 0,
    
            shippingMethod: context?.trackingInfo?.courierCode ?? '',
    
            replacements: (item.replacements ?? []).map(mapReplacement),
            returns: (item.returns  ?? []).map(mapReplacement),

            faultType: item.faultType != null ? String(item.faultType) : null,

            alternatives: Array.isArray(item.alternatives)
                ? item.alternatives.map(mapItemAlternative)
                : [],
        };
    }

    function mapItemAlternative(raw) {
        if (!raw || typeof raw !== 'object') {
            return { itemNumber: '', itemDescription: '', itemImageUrl: '' };
        }
        const img = raw.itemImage ?? raw.itemImageUrl ?? raw.image ?? null;
        return {
            itemNumber: String(raw.itemNumber ?? raw.alternativeItemNumber ?? ''),
            itemDescription: String(raw.itemDescription ?? raw.alternativeItemDescription ?? ''),
            itemImageUrl: img ? String(img) : '',
        };
    }

    function mapReplacementReturnItem(raw) {
        if (!raw || typeof raw !== 'object') return { quantity: 0 };
        return {
            quantity: parseInt(raw.quantity, 10) || 0,
        };
    }

    function mapReplacement(raw) {
        if (!raw || typeof raw !== 'object') {
            return {
                id: null,
                zendeskTicketId: null,
                orders: [],
                items: [],
                status: '',
            };
        }

        const mapped = {
            id: raw.id ?? null,
            zendeskTicketId: raw.zendeskTicketId != null ? String(raw.zendeskTicketId) : null,
            orders: raw.orders?.map(mapReplacementOrderEntry),
        };

        if (raw.parentItemQuantity != null) {
            mapped.parentItemQuantity = raw.parentItemQuantity;
        }
        if (Array.isArray(raw.items)) {
            mapped.items = raw.items.map(mapReplacementReturnItem);
        }
        if (raw.status != null) {
            mapped.status = String(raw.status);
        }
        if (raw.quantity != null || raw.returnQuantity != null) {
            mapped.quantity = parseInt(raw.quantity ?? raw.returnQuantity, 10) || 0;
        }

        return mapped;
    }

    function displayOrderDetails(orderData) {
        cachedOrderData = orderData;
        window.OrderAmendsFlow?.setCachedOrderData?.(orderData);
        toggleElementVisibility(selectors.formContainer, false);
        toggleElementVisibility(selectors.alertContainer, false);
        toggleElementVisibility(selectors.orderLayoutContainer, true);
        toggleElementVisibility('.order-layout__shipments-error-message', false);

        // Populate enhanced order details
        populateEnhancedOrderDetails(orderData);

        // Show order details container
        toggleElementVisibility(selectors.orderDetailsContainer, true);
    }

    function populateEnhancedOrderDetails(orderData) {
        try {
            // Order Number
            populateDataField({
                selector: '#order_number_display',
                value: orderData.orderSummary?.orderNo,
                parentElementSelector: '.order_number'
            });
            copyOrderNumberToDesktop();

            // Ordered date — UK-style day before month (API d/m/Y parsed as UTC calendar)
            const orderedDate = formatDate(orderData.orderSummary?.orderDate, 'j M Y');
            populateDataField({
                selector: '#ordered_date_display',
                value: orderedDate,
                parentElementSelector: '.order-header__ordered-date'
            });

            renderDeliveryTimeline(orderData);

            // Render shipment cards
            renderShipmentCards(orderData);
            
            // Populate order summary
            populateOrderSummary(orderData);

            // Render download invoice button
            renderDownloadInvoiceButton(orderData.orderSummary?.shopifyOrderInternalId);
            
            // Populate customer information
            populateCustomerInfo(orderData);
            notifyAddressUpdateOrderDisplayed(orderData);
        } catch (error) {
            console.error('Error in populateEnhancedOrderDetails:', error);
            showError(window.orderTrackerTranslations?.errorDisplayError);
        }
    }

    // Compare order shipping address with the address on a shipment row (fulfilledShipments[] element)
    function compareShippingAddresses(orderShippingAddress, shipmentShippingAddress) {
        // Return null if either address is missing
        if (!orderShippingAddress || !shipmentShippingAddress) {
            return null;
        }

        // Compare all relevant address fields
        const differences = {};
        let hasdifferences = false;

        // Define fields to compare
        const fieldsToCompare = [
            'address1',
            'address2', 
            'city',
            'state',
            'zipCode',
            'country',
            'company'
        ];

        fieldsToCompare.forEach(field => {
            const orderValue = (orderShippingAddress[field] || '').toString().trim();
            const shipmentValue = (shipmentShippingAddress[field] || '').toString().trim();
            
            if (orderValue !== shipmentValue) {
                differences[field] = {
                    order: orderValue,
                    shipment: shipmentValue
                };
                hasdifferences = true;
            }
        });

        return {
            isDifferent: hasdifferences,
            differences: differences,
            orderAddress: orderShippingAddress,
            shipmentAddress: shipmentShippingAddress
        };
    }

    function createUpdatedAddressHtml(mainShippingAddress, shipmentAddress) {
        const addressComparison = compareShippingAddresses(mainShippingAddress, shipmentAddress);
        if (! addressComparison?.isDifferent) return '';

        return `
            <div class="shipment__address">
                <div class="shipment__address-text"><strong>${escapeHtml(window.orderTrackerTranslations?.deliveryUpdatedAddress)}</strong> ${formatAddress(shipmentAddress)}</div>
                <div class="shipment__address-icon">
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="0.5" y="0.5" width="12" height="12" rx="6" stroke="#424442"/>
                        <path d="M6.05184 10V5.25H6.95184V10H6.05184ZM6.50184 4.3C6.33517 4.3 6.1985 4.24667 6.09184 4.14C5.98517 4.02667 5.93184 3.88667 5.93184 3.72C5.93184 3.56 5.98517 3.42667 6.09184 3.32C6.1985 3.20667 6.33517 3.15 6.50184 3.15C6.6685 3.15 6.80517 3.20667 6.91184 3.32C7.0185 3.42667 7.07184 3.56 7.07184 3.72C7.07184 3.88667 7.0185 4.02667 6.91184 4.14C6.80517 4.24667 6.6685 4.3 6.50184 4.3Z" fill="#424442"/>
                    </svg>
                </div>
            </div>
        `;
    }

    function renderDeliveryTimeline(orderData) {
        const deliveryTimeline = document.getElementById('delivery_timeline');
        const originalDeliveryDate = document.querySelector(selectors.originalDeliveryDate);
        
        if (!deliveryTimeline) return;

        // Clear existing timeline content
        deliveryTimeline.innerHTML = '';

        // Get order state information
        const orderState = getOrderState(orderData);
        const originalDates = getOriginalDeliveryDates(orderData);
        const orderItems = orderData.orderDetails?.orderItems;

        // Handle original delivery date display
        if (originalDates.hasRevisedDeliveryDates && originalDates.earliest && originalDates.latest) {
            const originalRange = formatDateRange(originalDates.earliest, originalDates.latest);

            originalDeliveryDate.textContent = `(${window.orderTrackerTranslations?.deliveryOriginalEstimate} ${originalRange})`;
            toggleElementVisibility(selectors.originalDeliveryDate, true);
        }

        // Hide original estimate when any despatched group is past the display threshold
        if (orderState.hasShipped && orderItems?.shipments?.length) {
            getShipmentOrBatchGroupsByDate(orderItems?.shipments).forEach(group => {
                if (isDatePastThreshold(group.latestDate, 10)) {
                    toggleElementVisibility(selectors.originalDeliveryDate, false);
                }
            });
        }

        // Build timeline entries based on current order state
        const timelineEntries = getDeliveryTimelineEntries(orderItems, orderState);

        deliveryTimeline.innerHTML = timelineEntries.map(entry =>
            getDeliveryTimelineDateHtml(entry.label, entry.dateRange, {
                delayMessage: entry.delayMessage,
                isCompleted: entry.isCompleted
            })
        ).join('');

    }

    // Render shipment cards based on status
    function renderShipmentCards(orderData) {
        const orderItems = orderData.orderDetails.orderItems;
        const container = document.getElementById('order_items_container');
        container.innerHTML = '';

        // Preserve status order while appending into one container.
        const orderedGroups = [
            { status: ORDER_STATUS_DESPATCHED, rows: orderItems.shipments || [] },
            { status: ORDER_STATUS_PREPARING_FOR_DESPATCH, rows: orderItems.preparingForDespatchShipments || [] },
            { status: ORDER_STATUS_OUTSTANDING_ITEMS, rows: orderItems.outstandingBatches || [] },
            { status: ORDER_STATUS_CANCELLED_ITEMS, rows: orderItems.cancelledItems || [] },
        ];

        orderedGroups.forEach(group => {
            if (group.rows.length > 0) {
                renderShipmentsByStatus(container, group.status, group.rows, orderData);
            }
        });

        window.OrderAmendsFlow?.rebuildOrderAmendsRegistry?.(orderData);
        window.OrderAmendsFlow?.updateOrderAmendsEditModeUi?.();
        window.OrderAmendsFlow?.updateOrderAmendsActivateCta?.(orderData);
        window.OrderReturnsFlow?.rebuildReturnsRegistry?.(orderData);
        window.OrderReturnsFlow?.updateReturnsCtaVisibility?.(orderData);
        window.OrderReportIssueFlow?.rebuildReportIssueRegistry?.(orderData);
        window.OrderReportIssueFlow?.updateReportIssueCtaVisibility?.(orderData);
        if (window.OrderReportIssueFlow?.isReportIssueFlowActive?.()) {
            window.OrderReportIssueFlow.syncSelectorLineUi?.();
        }
    }

    function renderShipmentsByStatus(container, status, shipments, orderData) {
        shipments.forEach((shipment, batchIndex) => {
            const shipmentCard = createShipmentCard(status, shipment, orderData, batchIndex);
            container.appendChild(shipmentCard);
        });
    }

    function isAddressUpdateConfigured() {
        const el = document.getElementById('order-tracking');
        const raw = (el?.dataset.orderAmendApiUrl || '').trim();
        return raw.toLowerCase().startsWith('https://');
    }

    function updateEditableShipmentAddress(orderData, shippingAddress, fulfillmentDocumentNo) {
        const oi = orderData?.orderDetails?.orderItems;
        const doc = String(fulfillmentDocumentNo || '').trim();
        if (!oi || !doc) return false;

        const buckets = [oi.preparingForDespatchShipments, oi.outstandingBatches];
        for (const arr of buckets) {
            if (!Array.isArray(arr)) continue;
            for (const batch of arr) {
                if (String(batch?.documentNo || '').trim() === doc) {
                    batch.shippingAddress = { ...(batch.shippingAddress || {}), ...shippingAddress };
                    return true;
                }
            }
        }
        return false;
    }

    function syncEditableShipmentAddresses(orderData, shippingAddress) {
        const oi = orderData?.orderDetails?.orderItems;
        if (!oi) return;
        [...(oi.preparingForDespatchShipments || []), ...(oi.outstandingBatches || [])].forEach(batch => {
            batch.shippingAddress = { ...(batch.shippingAddress || {}), ...shippingAddress };
        });
    }

    function getOrderAmendsBucketKey(status) {
        if (status === ORDER_STATUS_PREPARING_FOR_DESPATCH) return 'preparing';
        if (status === ORDER_STATUS_OUTSTANDING_ITEMS) return 'outstanding';
        return null;
    }

    function lookupShipmentBatchFromOrderData(orderData, bucket, indexStr) {
        const oi = orderData?.orderDetails?.orderItems;
        if (!oi) return null;
        const idx = parseInt(indexStr, 10);
        if (!Number.isFinite(idx) || idx < 0) return null;

        if (bucket === 'preparing') {
            const batch = oi.preparingForDespatchShipments?.[idx];
            return batch ? { status: ORDER_STATUS_PREPARING_FOR_DESPATCH, batch } : null;
        }
        if (bucket === 'outstanding') {
            const batch = oi.outstandingBatches?.[idx];
            return batch ? { status: ORDER_STATUS_OUTSTANDING_ITEMS, batch } : null;
        }
        return null;
    }

    function getShipmentCardBodyHtml(status, shipmentOrBatch, orderData, itemsInnerHtml, batchIndex = 0) {
        const icon = getIconForStatus(status, shipmentOrBatch?.trackingInfo?.courierCode);
        const subtitle = getSubtitleForStatus(status, shipmentOrBatch);
        const mainShippingAddress = orderData.orderDetails?.shippingAddress;
        const shipmentAddress = shipmentOrBatch.shippingAddress;
        const addressHtml = createUpdatedAddressHtml(mainShippingAddress, shipmentAddress);
        const warehouseNoticeHtml = getWarehouseNoticeBannerHtml();
        const addressUpdateHtml = getAddressUpdateShipmentHtml(status, shipmentOrBatch);
        const itemsHtml =
            itemsInnerHtml && itemsInnerHtml.length > 0
                ? `<div class="shipment__items">${itemsInnerHtml}</div>`
                : '';
        const shipmentCardHeaderHtml = getShipmentCardHeaderHtml(status, shipmentOrBatch, orderData);

        const reportAllHtml = getReportAllShipmentHtml(status, shipmentOrBatch, batchIndex);

        return `
            <div class="shipment__header">
                ${shipmentCardHeaderHtml}
                <div class="shipment__status-row">
                    <div class="shipment__icon">${icon}</div>
                    <div class="shipment__status-title">
                        <h3 class="shipment__title">${escapeHtml(status)}</h3>
                        <p class="shipment__subtitle">${subtitle}</p>
                    </div>
                </div>
            </div>
            <!-- Warehouse Notice -->
            ${[ORDER_STATUS_PREPARING_FOR_DESPATCH].includes(status) ? warehouseNoticeHtml : ''}
            <!-- End Warehouse Notice -->

            <!-- Updated Address -->
            ${addressHtml}
            <!-- End Updated Address -->

            <!-- Report Shipment All -->
            ${reportAllHtml}
            <!-- End Report Shipment All -->

            <!-- Address Update -->
            ${[ORDER_STATUS_PREPARING_FOR_DESPATCH, ORDER_STATUS_OUTSTANDING_ITEMS].includes(status) ? addressUpdateHtml : ''}
            <!-- End Address Update -->

            <!-- Items -->
            ${itemsHtml}
            <!-- End Items -->
        `;
    }
    
    function getReportAllShipmentHtml(status, shipmentOrBatch, batchIndex = 0) {
        if (status !== ORDER_STATUS_DESPATCHED) return '';
        if (!window.OrderReturnsFlow?.isShipmentEligibleForReturn?.(shipmentOrBatch)) return '';
        if (!window.OrderReturnsFlow?.shipmentHasReturnableItems?.(shipmentOrBatch)) return '';

        const label = window.orderTrackerTranslations?.returnReportAll || 'Return All';

        return `
            <div class="shipment__action-banner shipment__report-all" data-action-banner="report-all">
                <p class="shipment__action-banner-label">
                    <a href="javascript:void(0)" class="shipment__report-all-btn"
                        data-report-all-batch-index="${escapeHtmlAttr(String(batchIndex))}">
                        ${escapeHtml(label)}
                    </a>
                </p>
            </div>
        `;
    }

    function getAddressUpdateShipmentHtml(status, shipmentOrBatch) {
        if (!isAddressUpdateConfigured()) return '';

        const docNo = String(shipmentOrBatch?.documentNo || '').trim();
        if (!docNo) return '';

        const label = window.orderTrackerTranslations?.addressUpdateCta || 'Update delivery address';
        return `
            <div class="shipment__address-update shipment__action-banner">
                <p class="shipment__address-update-label">Change shipping address? <a href="javascript:void(0)" class="shipment__update-address-btn"
                    data-fulfillment-document-no="${escapeHtmlAttr(docNo)}">
                    ${escapeHtml(label)}?
                </a>
                </p>
            </div>
        `;
    }

    function getIconForStatus(status, courierCode = '') {
        switch (status) {
            case ORDER_STATUS_DESPATCHED: 
                if (courierCode) { 
                    switch (courierCode) {
                        case 'BJS': return window.shippingIcons.bjs;
                        case 'DHL': return window.shippingIcons.dhl;
                        case 'DPD': return window.shippingIcons.dpd;
                        case 'HER': return window.shippingIcons.evri;
                        case 'XDP': return window.shippingIcons.evri;
                        case 'EVRI': return window.shippingIcons.evri;
                        case 'POST': return window.shippingIcons.royalMail;
                        case 'AGTC': return window.shippingIcons.lj;
                        case 'LJ': return window.shippingIcons.lj;
                        default: window.shippingIcons.despatched;
                    }
                }

                return window.shippingIcons.despatched;
            case ORDER_STATUS_PREPARING_FOR_DESPATCH: 
                return window.shippingIcons.preparing;
            case ORDER_STATUS_OUTSTANDING_ITEMS: 
                return window.shippingIcons.outstanding;
            case ORDER_STATUS_CANCELLED_ITEMS: 
                return window.shippingIcons.cancelled;
            default: 
                return window.shippingIcons.outstanding;
        }
    }

    function getStatusModifier(status) {
        switch (status) {
            case ORDER_STATUS_DESPATCHED: return 'shipment--despatched';
            case ORDER_STATUS_PREPARING_FOR_DESPATCH: return 'shipment--preparing';
            case ORDER_STATUS_OUTSTANDING_ITEMS: return 'shipment--outstanding';
            case ORDER_STATUS_CANCELLED_ITEMS: return 'shipment--cancelled';
            default: return '';
        }
    }

    function createShipmentCard(status, shipmentOrBatch, orderData, batchIndex = 0) {
        const card = document.createElement('div');
        const statusModifier = getStatusModifier(status);
        const bucket = getOrderAmendsBucket(status) ?? (status === ORDER_STATUS_DESPATCHED ? 'despatched' : null);
        const amendableCard = bucket === 'preparing' || bucket === 'outstanding' ? ' shipment--order-amends-card' : '';
        const returnsEligible =
            status === ORDER_STATUS_DESPATCHED
            && window.OrderReturnsFlow?.isShipmentEligibleForReturn?.(shipmentOrBatch);
        card.className = `shipment ${statusModifier}${amendableCard}${returnsEligible ? ' shipment--returns-eligible' : ''}`;

        const itemsInner =
            shipmentOrBatch.items && shipmentOrBatch.items.length > 0
                ? shipmentOrBatch.items
                      .map((item, itemIndex) =>
                          createShipmentItemHtml(
                              item,
                              status,
                              bucket,
                              batchIndex,
                              itemIndex,
                              shipmentOrBatch
                          )
                      )
                      .join('')
                : '';

        card.innerHTML =
            getShipmentCardBodyHtml(status, shipmentOrBatch, orderData, itemsInner, batchIndex);
            // getAmendShipmentFooterHtml(status, batchIndex);

        return card;
    }

    function getShipmentCardHeaderHtml(status, shipmentOrBatch, orderData) {
        // Get shipment ID and date
        const shipmentId = shipmentOrBatch.documentNo || null;
        const shipmentDate = getShipmentOrBatchDate(status, shipmentOrBatch, orderData);
        const tr = window.orderTrackerTranslations;

        if (status != ORDER_STATUS_CANCELLED_ITEMS) {
                const shipmentIdHtml = shipmentId ? `
                    <div class="shipment-card__header-column shipment-card__shipment-id">
                        <p>${escapeHtml(tr?.shipment)}</p>
                        <strong>${escapeHtml(shipmentId)}</strong>
                    </div>
                ` : '';
                const shipmentDateHtml = shipmentDate ? `
                    <div class="shipment-card__header-column shipment-card__delivery-date">
                        <p>${escapeHtml(tr.deliveryEstDelivery)}</p>
                        <strong>${escapeHtml(formatShipmentDateDisplay(shipmentDate))}</strong>
                    </div>
                ` : '';

                const shipmentCardHeaderHtml = `
                    <div class="shipment__header-column shipment__id-date flex ${!shipmentIdHtml ? 'shipment__no-shipment-id' : ''}">
                        ${shipmentIdHtml}
                        ${shipmentDateHtml}
                    </div>
                `;

            return shipmentCardHeaderHtml;
        } else {
            return '';
        }
    }

    // Helper function to get date ranges for shipments using timeline logic
    function getShipmentOrBatchDateRange(shipmentOrBatch) {
        const { earliestDate, latestDate } = getEarliestAndLatestDate(shipmentOrBatch);
        if (!earliestDate || !latestDate) return null;

        return formatDateRange(earliestDate, latestDate);
    }

    function getShipmentOrBatchDate(status, shipmentOrBatch) {
        // Try to get date range from helper function for preparing and outstanding items
        if (status !== ORDER_STATUS_CANCELLED_ITEMS) {
            const dateRange = getShipmentOrBatchDateRange(shipmentOrBatch);

            if (dateRange) {
                return dateRange;
            }
        }

        // Fallback logic
        switch (status) {
            case ORDER_STATUS_DESPATCHED:
                return shipmentOrBatch.earliestDeliveryDate || '';
            case ORDER_STATUS_PREPARING_FOR_DESPATCH:
                return shipmentOrBatch.earliestDeliveryDate || '';
            case ORDER_STATUS_OUTSTANDING_ITEMS:
                return shipmentOrBatch.estimatedDeliveryDate || '';
            case ORDER_STATUS_CANCELLED_ITEMS:
                return shipmentOrBatch.cancellationDate || '';
            default:
                return shipmentOrBatch.date || '';
        }
    }

    function getSubtitleForStatus(status, shipmentOrBatch) {
        switch (status) {
            case ORDER_STATUS_DESPATCHED:
                const trackingInfo = shipmentOrBatch?.trackingInfo;

                const trackingNumber = trackingInfo?.trackingNumbers || trackingInfo?.trackingNumber;

                if (trackingNumber) {
                    const label = escapeHtml(window.orderTrackerTranslations?.deliveryTrackingNumber);
                    const num = escapeHtml(String(trackingNumber));
                    if (trackingInfo?.trackingNumberUrl) {
                        const safeUrl = sanitizeHref(trackingInfo.trackingNumberUrl);
                        if (safeUrl) {
                            return `${label} <a href="${escapeHtmlAttr(safeUrl)}" target="_blank" rel="noopener noreferrer">${num}</a>`;
                        }
                    }
                    return `${label} ${num}`;
                }

                return escapeHtml(window.orderTrackerTranslations?.deliveryItemsDespatched);
            case ORDER_STATUS_PREPARING_FOR_DESPATCH:
            case ORDER_STATUS_OUTSTANDING_ITEMS:
            case ORDER_STATUS_CANCELLED_ITEMS:
            default:
                return '';
        }
    }

    function getOrderAmendsBucket(status) {
        if (status === ORDER_STATUS_PREPARING_FOR_DESPATCH) return 'preparing';
        if (status === ORDER_STATUS_OUTSTANDING_ITEMS) return 'outstanding';
        return null;
    }

    function makeOrderAmendsLineKey(bucket, batchIndex, itemIndex) {
        return `${bucket}:${batchIndex}:${itemIndex}`;
    }

    function makeOrderAmendsItemDomId(bucket, batchIndex, itemIndex) {
        return `oa-line-${bucket}-${batchIndex}-${itemIndex}`;
    }

    function createOrderAmendsItemHtml(item, status, bucket, batchIndex, itemIndex, shipmentOrBatch, imageHtml) {
        const tr = window.orderTrackerTranslations || {};
        const lineKey = makeOrderAmendsLineKey(bucket, batchIndex, itemIndex);
        const lineDomId = makeOrderAmendsItemDomId(bucket, batchIndex, itemIndex);
        const maxQty = Math.max(0, parseInt(item.quantity, 10) || 0);
        const grossAmount = parseNumber(item.grossAmount);
        const unitAmount = maxQty > 0 ? grossAmount / maxQty : 0;
        const qtyLabelTemplate = tr.orderAmendsQuantityLabelTemplate || 'Quantity: {{ quantity }}';
        const qtyLabel = qtyLabelTemplate.replace(/\{\{\s*quantity\s*\}\}/g, String(maxQty));
        const removeLabel = tr.orderAmendsRemoveProduct || 'Remove';
        const backLabel = tr.orderAmendsCancellationBack || tr.orderAmendsCancelReasonBack || 'Back';
        const confirmLabel = tr.orderAmendsQtyConfirmQuestion || 'Confirm?';
        const discardLabel = tr.orderAmendsRemoveReasonDiscard || tr.orderAmendsRemoveDiscard || 'Discard';
        const nextLabel = tr.orderAmendsRemoveReasonNext || 'Next';
        const undoLabel = tr.orderAmendsRemoveUndo || 'Undo';
        const reasonPlaceholder = tr.orderAmendsRemoveReasonPlaceholder || 'Please select a reason';
        const qtyStepperLabel = tr.orderAmendsQtyStepperLabel || 'Quantity';
        const chooseDifferentProductLabel = tr.orderAmendsChooseButton || 'Choose a different product';
        const minusShowsTrash = maxQty <= 1;

        return `
            <div class="shipment__item-container shipment__item-container--order-amends"
                data-oa-line-key="${escapeHtmlAttr(lineKey)}"
                data-oa-line-dom-id="${escapeHtmlAttr(lineDomId)}">
                <div class="shipment__item-container-inner">
                <div class="shipment__item">
                    <div class="shipment__item-image-container">
                        ${imageHtml}
                    </div>
                    <div class="shipment__item-info">
                        <p class="shipment__item-name">${escapeHtml(item.itemDescription)}</p>
                        <p data-oa-qty-label>${escapeHtml(qtyLabel)}</p>
                        <p class="shipment__item-amount">
                            <span data-oa-display-price>${escapeHtml(formatMoney(grossAmount))}</span>
                            <span class="hidden" data-oa-display-original></span>
                        </p>
                    </div>
                </div>
                <div class="shipment__item-product-actions">
                    <div class="shipment__item-product-actions-inner">
                        <div class="shipment__item-product-actions-amend-state hidden order-amends__item-actions">
                            <div data-oa-action-slot>
                                <button type="button" class="button button--secondary hidden order-amends__reason-discard-btn" data-oa-qty-discard-btn>
                                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M9.6 5.4L5.4 9.6M5.4 5.4L9.6 9.6M14.5 7.5C14.5 11.366 11.366 14.5 7.5 14.5C3.63401 14.5 0.5 11.366 0.5 7.5C0.5 3.63401 3.63401 0.5 7.5 0.5C11.366 0.5 14.5 3.63401 14.5 7.5Z" stroke="#5C5F5C" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    ${escapeHtml(discardLabel)}
                                </button>
                                <button type="button" class="button button--secondary hidden order-amends__reason-discard-btn" data-oa-qty-back-btn>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M10.8 8H5.2M8 5.2L5.2 8L8 10.8M1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8Z" stroke="#424442" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    ${escapeHtml(backLabel)}
                                </button>
                            </div>
                            <button type="button" class="button button--secondary order-amends__choose-product-btn"
                                data-oa-choose-product data-oa-line-key="${escapeHtmlAttr(lineKey)}">
                                ${escapeHtml(chooseDifferentProductLabel)}
                            </button>
                            <div class="hidden" data-oa-qty-reason-field>
                                <select class="order-amends__reason-select" data-oa-qty-reason-select
                                    aria-label="${escapeHtmlAttr(reasonPlaceholder)}">
                                    <option value="">${escapeHtml(reasonPlaceholder)}</option>
                                </select>
                            </div>
                        </div>
                        <div class="shipment__item-product-actions-remove-active hidden order-amends__item-actions order-amends__reason-flow" data-oa-remove-active>
                            <p class="order-amends__reason-intro medium-up-hide" data-oa-reason-intro></p>
                            <select class="order-amends__reason-select" data-oa-reason-select aria-label="${escapeHtmlAttr(reasonPlaceholder)}">
                                <option value="">${escapeHtml(reasonPlaceholder)}</option>
                            </select>
                            <div class="order-amends__reason-actions">
                                <button type="button" class="button button--secondary order-amends__reason-discard-btn" data-oa-discard-btn>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M10.8 8H5.2M8 5.2L5.2 8L8 10.8M1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8Z" stroke="#424442" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <span>${escapeHtml(backLabel)}</span>
                                </button>
                                <button type="button" class="button button--primary order-amends__qty-confirm-btn order-amends__reason-next-btn" data-oa-reason-confirm disabled>
                                    <span>${escapeHtml(confirmLabel)}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
                <div class="order-amends__remove-summary-step hidden"
                    data-oa-summary-step>
                    <span data-oa-summary-text></span>
                    <button type="button" class="button button--secondary" data-oa-remove-undo>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M3.7251 0.599609L0.600098 3.72461L3.7251 6.84961M0.600098 3.72461H7.1626C7.61402 3.72461 8.06102 3.81352 8.47807 3.98627C8.89513 4.15902 9.27408 4.41223 9.59328 4.73143C9.91248 5.05063 10.1657 5.42958 10.3384 5.84663C10.5112 6.26369 10.6001 6.71069 10.6001 7.16211C10.6001 7.61353 10.5112 8.06053 10.3384 8.47758C10.1657 8.89464 9.91248 9.27359 9.59328 9.59279C9.27408 9.91199 8.89513 10.1652 8.47807 10.3379C8.06102 10.5107 7.61402 10.5996 7.1626 10.5996H4.9751" stroke="#424442" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        ${escapeHtml(undoLabel)}
                    </button>
                </div>
                <div class="order-amends__validation-error hidden" data-oa-validation-error role="alert">
                    <p class="order-amends__validation-error-message">
                        <svg class="order-amends__validation-error-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.2"/>
                            <path d="M8 4.5V8.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
                            <circle cx="8" cy="11" r="0.75" fill="currentColor"/>
                        </svg>
                        <span data-oa-validation-error-text></span>
                    </p>
                    <button type="button" class="button button--secondary order-amends__validation-undo-btn" data-oa-validation-undo>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M3.7251 0.599609L0.600098 3.72461L3.7251 6.84961M0.600098 3.72461H7.1626C7.61402 3.72461 8.06102 3.81352 8.47807 3.98627C8.89513 4.15902 9.27408 4.41223 9.59328 4.73143C9.91248 5.05063 10.1657 5.42958 10.3384 5.84663C10.5112 6.26369 10.6001 6.71069 10.6001 7.16211C10.6001 7.61353 10.5112 8.06053 10.3384 8.47758C10.1657 8.89464 9.91248 9.27359 9.59328 9.59279C9.27408 9.91199 8.89513 10.1652 8.47807 10.3379C8.06102 10.5107 7.61402 10.5996 7.1626 10.5996H4.9751" stroke="#424442" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        ${escapeHtml(undoLabel)}
                    </button>
                </div>
                <div class="hidden" data-oa-cancellation-panel aria-hidden="true">
                    <div class="hidden" data-oa-reason-step></div>
                    <div class="hidden" data-oa-retention-step></div>
                </div>
            </div>
        `;
    }

    function createShipmentItemHtml(item, status, bucket, batchIndex, itemIndex, shipmentOrBatch) {
        const hasImage = item.itemImageUrl && item.itemImageUrl.trim() !== '';
        const safeImgSrc = hasImage ? sanitizeHref(item.itemImageUrl) : '';
        const imageHtml = hasImage && safeImgSrc ?
            `<img src="${escapeHtmlAttr(safeImgSrc)}" alt="${escapeHtmlAttr(item.itemDescription)}" class="shipment__item-image">` :
            `<div class="shipment__item-placeholder shipment__item-image"></div>`;

        const assemblyHref = item.assemblyInstructionRequired && item.assemblyInstructionUrl
            ? sanitizeHref(item.assemblyInstructionUrl)
            : '';
        const assemblyLink = assemblyHref ?
            `<a href="${escapeHtmlAttr(assemblyHref)}" class="shipment__item-assembly-btn shipment__item-link" target="_blank" rel="noopener noreferrer" download>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <path d="M9.11977 3.67028C8.9848 3.80798 8.9092 3.99311 8.9092 4.18593C8.9092 4.37875 8.9848 4.56389 9.11977 4.70159L10.2984 5.88023C10.4361 6.0152 10.6212 6.0908 10.8141 6.0908C11.0069 6.0908 11.192 6.0152 11.3297 5.88023L14.1069 3.10306C14.4773 3.92161 14.5895 4.83362 14.4284 5.71753C14.2674 6.60145 13.8407 7.41529 13.2054 8.0506C12.5701 8.68591 11.7563 9.11252 10.8724 9.27357C9.98846 9.43462 9.07645 9.32247 8.25789 8.95206L3.16764 14.0423C2.87459 14.3354 2.47712 14.5 2.06267 14.5C1.64822 14.5 1.25075 14.3354 0.957695 14.0423C0.664638 13.7492 0.5 13.3518 0.5 12.9373C0.5 12.5229 0.664638 12.1254 0.957695 11.8324L6.04794 6.74211C5.67753 5.92355 5.56538 5.01154 5.72643 4.12763C5.88748 3.24371 6.31409 2.42987 6.9494 1.79456C7.58471 1.15925 8.39855 0.732642 9.28247 0.571591C10.1664 0.410539 11.0784 0.522694 11.8969 0.893107L9.12714 3.66291L9.11977 3.67028Z" stroke="#424442" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
                <span class="small-hide">${escapeHtml(window.orderTrackerTranslations?.actionsAssemblyInstructions)}</span>
                <span class="medium-up-hide">${escapeHtml(window.orderTrackerTranslations?.actionsAssemblyInstructionsMobile)}</span>
            </a>` : '';

        const replacementsHtml = createReplacementsHtml(item, status);
        const returnQuantity = window.OrderReturnsFlow?.getReturnedQuantity?.(item)
            ?? getReturnsTotalQuantity(item.returns || []);
        const returnQuantityHtml = returnQuantity
            ? `(${escapeHtml(window.orderTrackerTranslations?.returned)} ${escapeHtml(returnQuantity)})`
            : '';

        const isAmendable = bucket === 'preparing' || bucket === 'outstanding';
        if (isAmendable) {
            return createOrderAmendsItemHtml(item, status, bucket, batchIndex, itemIndex, shipmentOrBatch, imageHtml);
        }

        const isDespatchedReturnContext =
            status === ORDER_STATUS_DESPATCHED
            && bucket === 'despatched'
            && window.OrderReturnsFlow?.isShipmentEligibleForReturn?.(shipmentOrBatch);

        const isReturnsEligibleLine =
            isDespatchedReturnContext
            && window.OrderReturnsFlow?.isLineEligibleForReturn?.(item, shipmentOrBatch);

        const isFullyReturnedLine =
            isDespatchedReturnContext
            && !isReturnsEligibleLine
            && (window.OrderReturnsFlow?.getReturnedQuantity?.(item) ?? 0) > 0;

        const lineKey = isReturnsEligibleLine
            ? window.OrderReturnsFlow.makeLineKey(batchIndex, itemIndex)
            : '';
        const maxQty = window.OrderReturnsFlow?.getReturnableQuantity?.(item)
            ?? (parseInt(item.quantity, 10) || 0);

        const returnsControlsHtml = isReturnsEligibleLine
            ? `
                <div class="order-returns__item-controls" data-oa-return-controls data-return-line-key="${escapeHtmlAttr(lineKey)}">
                    <button type="button" class="order-returns__add-btn" data-oa-return-add data-return-line-key="${escapeHtmlAttr(lineKey)}" aria-label="Add to return">
                        <span>${escapeHtml(window.orderTrackerTranslations?.returnReport || 'Return')}</span>
                    </button>
                    <div class="order-amends__returns-qty-container hidden">
                        <span class="order-amends__returns-qty-label">${escapeHtml(window.orderTrackerTranslations?.returnReportIssues || 'Return Quantity')}</span>
                        <div class="order-amends__returns-qty" data-oa-return-qty data-return-line-key="${escapeHtmlAttr(lineKey)}" data-max-qty="${maxQty}">
                            <button type="button" class="order-amends__returns-qty-btn" data-oa-return-qty-step="-1" aria-label="Decrease quantity">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="2" viewBox="0 0 12 2" fill="none">
                                    <path d="M10.7002 0C11.0868 0 11.4004 0.313596 11.4004 0.700195C11.4004 1.08679 11.0868 1.40039 10.7002 1.40039H0.700195C0.313708 1.40026 0 1.08671 0 0.700195C0 0.313677 0.313708 0.000131992 0.700195 0H10.7002Z" fill="#5C5F5C"/>
                                </svg>
                            </button>
                            <span class="order-amends__returns-qty-fraction" data-oa-return-qty-current>0</span>
                            <button type="button" class="order-amends__returns-qty-btn" data-oa-return-qty-step="1" aria-label="Increase quantity">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M5.7002 0C6.08679 0 6.40039 0.313596 6.40039 0.700195V5H10.7002C11.0868 5 11.4004 5.3136 11.4004 5.7002C11.4004 6.08679 11.0868 6.40039 10.7002 6.40039H6.40039V10.7002C6.40039 11.0868 6.08679 11.4004 5.7002 11.4004C5.3136 11.4004 5 11.0868 5 10.7002V6.40039H0.700195C0.313596 6.40039 0 6.08679 0 5.7002C0 5.3136 0.313596 5 0.700195 5H5V0.700195C5 0.313596 5.3136 0 5.7002 0Z" fill="#5C5F5C"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>`
            : isFullyReturnedLine
                ? `<span class="order-returns__returned-label">${escapeHtml(window.orderTrackerTranslations?.returned || 'Returned')}</span>`
                : '';

        const returnsDetailCardHtml = isReturnsEligibleLine
            ? `
                <div class="order-returns__detail-card hidden" data-oa-return-detail-card data-return-line-key="${escapeHtmlAttr(lineKey)}" hidden>
                    <button type="button" class="order-returns__picker-field" data-oa-return-open-drawer="productStatus" data-return-line-key="${escapeHtmlAttr(lineKey)}" aria-haspopup="dialog">
                        <span class="order-returns__picker-value" data-oa-return-product-status-label>${window.orderTrackerTranslations?.returnSelectProductStatus || 'Select a condition'}*</span>
                        <span class="order-returns__picker-chevron" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="#424442" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </span>
                    </button>
                    <div class="shipment__item-action-group">
                        <p class="shipment__item-action-group-label">${escapeHtml(window.orderTrackerTranslations?.returnOptional || 'Optional')}</p>
                        <button type="button" class="order-returns__picker-field" data-oa-return-open-drawer="issue" data-return-line-key="${escapeHtmlAttr(lineKey)}" aria-haspopup="dialog">
                            <span class="order-returns__picker-value" data-oa-return-issue-label>${window.orderTrackerTranslations?.returnSelectReasonPlaceholder || 'Select a reason'}</span>
                            <span class="order-returns__picker-chevron" aria-hidden="true">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="#424442" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </span>
                        </button>
                    </div>
                </div>`
            : '';

        const returnsErrorBannerHtml = isReturnsEligibleLine
            ? `
                <div class="order-returns__item-error hidden" data-oa-return-item-error data-return-line-key="${escapeHtmlAttr(lineKey)}" role="alert" hidden>
                    <span class="order-returns__item-error-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="7.25" stroke="currentColor" stroke-width="1.5"/>
                            <path d="M8 4.5V8.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            <circle cx="8" cy="11" r="0.75" fill="currentColor"/>
                        </svg>
                    </span>
                    <p class="order-returns__item-error-text"></p>
                </div>`
            : '';

        const isReportIssueContext =
            status === ORDER_STATUS_DESPATCHED
            && bucket === 'despatched'
            && window.OrderReportIssueFlow?.isShipmentEligibleForReportIssue?.(shipmentOrBatch);

        const isReportIssueEligibleLine =
            isReportIssueContext
            && window.OrderReportIssueFlow?.isLineEligibleForReportIssue?.(item);

        const reportIssueLineKey = isReportIssueEligibleLine
            ? window.OrderReportIssueFlow.makeLineKey(batchIndex, itemIndex)
            : '';

        const reportIssueLineQty = isReportIssueEligibleLine
            ? Math.max(1, parseInt(item.quantity, 10) || 1)
            : 0;

        const reportIssueControlsHtml = isReportIssueEligibleLine
            ? `
                <p class="order-report-issue__line-qty shipment__item-quantity" data-oa-report-remaining-qty data-oa-report-line-qty="${reportIssueLineQty}">${escapeHtml(
                    (window.orderTrackerTranslations?.reportIssueRemainingQtyLine || 'Quantity: {{ qty }}').replace(
                        '{{ qty }}',
                        String(reportIssueLineQty)
                    )
                )}</p>
                <div class="order-report-issue__item-controls" data-oa-report-controls data-oa-report-line-key="${escapeHtmlAttr(reportIssueLineKey)}" data-oa-report-line-qty="${reportIssueLineQty}">
                    <button type="button" class="order-report-issue__report-btn" data-oa-report-action="open-drawer" data-oa-report-line-key="${escapeHtmlAttr(reportIssueLineKey)}">
                        ${escapeHtml(window.orderTrackerTranslations?.reportIssueBtn || 'Report Issue')}
                    </button>
                </div>`
            : `
                <div class="order-report-issue__item-controls" data-oa-report-controls data-oa-report-line-key="${escapeHtmlAttr(reportIssueLineKey)}" data-oa-report-line-qty="${reportIssueLineQty}">
                    <button type="button" class="order-report-issue__report-btn order-report-issue__report-btn--exhausted">
                        ${escapeHtml(window.orderTrackerTranslations?.reportIssueAllReported)}
                    </button>
                </div>
            `;

        const containerClassSuffix = [
            isReturnsEligibleLine ? ' shipment__item-container--returns' : '',
            isFullyReturnedLine ? ' shipment__item-returns--not-eligible' : '',
            isReportIssueEligibleLine ? ' shipment__item-container--report-issue' : ' shipment__item-container--report-issue-exhausted',
        ].join('');

        const dataLineAttrs = [
            lineKey ? ` data-oa-return-line-key="${escapeHtmlAttr(lineKey)}"` : '',
            reportIssueLineKey ? ` data-oa-report-line-key="${escapeHtmlAttr(reportIssueLineKey)}"` : '',
        ].join('');

        return `
            <div class="shipment__item-container${containerClassSuffix}"${dataLineAttrs}>
                <div class="shipment__item-container-inner">
                    <div class="shipment__item">
                        <div class="shipment__item-image-container">
                            ${imageHtml}
                        </div>
                        <div class="shipment__item-info">
                            <p class="shipment__item-name">${escapeHtml(item.itemDescription)}</p>
                            <p class="shipment__item-quantity">${escapeHtml(window.orderTrackerTranslations?.orderAmendsCancellationSummaryQuantity || 'Quantity')}: ${escapeHtml(item.quantity)} ${returnQuantityHtml}</p>
                            ${`<p class="shipment__item-amount">${escapeHtml(formatMoney(item.grossAmount))}</p>`}
                            ${returnsControlsHtml}
                            ${reportIssueControlsHtml}
                        </div>
                    </div>
                    ${returnsDetailCardHtml}
                    ${(assemblyLink && status == ORDER_STATUS_DESPATCHED) || replacementsHtml ? `
                    <div class="shipment__item-links shipment__item-product-actions">
                        ${assemblyLink && status == ORDER_STATUS_DESPATCHED ? assemblyLink : ''}
                        ${replacementsHtml}
                    </div>`: ''}
                </div>
                ${returnsErrorBannerHtml}
            </div>
        `;
    }

    function getWarehouseNoticeBannerHtml() {
        const showWarehouseNoticeBanner = document.getElementById('order-tracking')?.dataset.showWarehouseNoticeBanner === 'true';
        if (!showWarehouseNoticeBanner) return '';

        return `
            <div class="shipment__item-warehouse-notice-banner shipment__action-banner">
                <p class="shipment__action-banner-label">
                    <strong>${escapeHtml(window.orderTrackerTranslations?.pleaseNote)}</strong> ${escapeHtml(window.orderTrackerTranslations?.warehouseNotice)}
                </p>
            </div>
        `;
    }

    function createReplacementItemHtml(item) {
        const reasonHtml = item.faultType ? replaceSmartTags(
            window.orderTrackerTranslations?.replacementsReasonHtml, {reason: item.faultType}
        ) : '';

        return `
            <p class="shipment__item-name">${escapeHtml(item.quantity)}x ${escapeHtml(item.itemDescription)} ${reasonHtml}</p>
        `;
    }

    /** Sorts replacement groups by earliest shipment date while keeping status context. */
    function getReplacementOrderShipmentSequence(order) {
        const oi = order.orderDetails?.orderItems;
        if (!oi) return [];
        const sequence = Array.isArray(oi.shipmentSequence) && oi.shipmentSequence.length > 0
            ? [...oi.shipmentSequence]
            : [
                ...(oi.shipments || []).map(batch => ({ status: ORDER_STATUS_DESPATCHED, batch })),
                ...(oi.preparingForDespatchShipments || []).map(batch => ({ status: ORDER_STATUS_PREPARING_FOR_DESPATCH, batch })),
                ...(oi.outstandingBatches || []).map(batch => ({ status: ORDER_STATUS_OUTSTANDING_ITEMS, batch })),
            ];

        (oi.cancelledItems || []).forEach(batch => {
            sequence.push({ status: ORDER_STATUS_CANCELLED_ITEMS, batch });
        });

        return sequence;
    }

    function createReplacementShipmentGroupHtml(order, status, shipmentOrBatch) {
        const statusModifier = getStatusModifier(status);
        const icon = getIconForStatus(status, shipmentOrBatch?.trackingInfo?.courierCode);
        const warehouseNoticeHtml = getWarehouseNoticeBannerHtml();
        const subtitle = getSubtitleForStatus(status, shipmentOrBatch);
        const orderDataStub = { orderDetails: order.orderDetails };
        const shipmentCardHeaderHtml = getShipmentCardHeaderHtml(status, shipmentOrBatch, orderDataStub);

        const itemsHtml =
            shipmentOrBatch.items && shipmentOrBatch.items.length > 0
                ? `
                <div class="shipment__items">
                    <div class="shipment-item__replacement-items">
                        ${[ORDER_STATUS_PREPARING_FOR_DESPATCH].includes(status) ? warehouseNoticeHtml : ''}
                        ${shipmentOrBatch.items.map(item => createReplacementItemHtml(item)).join('')}
                    </div>
                </div>
            `
                : '';

        return `
            <div class="replacement-order__shipment-group shipment ${statusModifier}">
                <div class="shipment__header">
                    ${shipmentCardHeaderHtml}
                    <div class="shipment__status-row">
                        <div class="shipment__icon">${icon}</div>
                        <div class="shipment__status-title">
                            <h3 class="shipment__title">${escapeHtml(status)}</h3>
                            <p class="shipment__subtitle">${subtitle}</p>
                        </div>
                    </div>
                </div>
                ${itemsHtml}
            </div>
        `;
    }

    function renderReplacementOrderShipmentItemsHtml(order) {
        return getReplacementOrderShipmentSequence(order)
            .map(({ status, batch }) => createReplacementShipmentGroupHtml(order, status, batch))
            .join('');
    }

    function createReplacementOrdersLayoutHtml(replacement) {
        if (replacement.orders.length === 0) return '';
        const ticketId = replacement.zendeskTicketId;

        let html = '';

        for (const order of replacement.orders || []) {
            const groupsHtml = renderReplacementOrderShipmentItemsHtml(order);
            if (!groupsHtml) continue;

            html += `
                <div class="shipment__item-replacement-body">
                    <div class="replacement-order-line-items">
                        ${groupsHtml}
                    </div>
                </div>
            `;
        }

        return `
            <div class="shipment-item__replacement">
                <div class="shipment-item__replacement-header">
                    <div class="shipment-item__replacement-ticket-id">
                        ${escapeHtml(window.orderTrackerTranslations?.replacementsTicket)} #${escapeHtml(ticketId)}
                    </div>
                </div>
                ${html}
            </div>
        `;
    }

    function createReplacementsHtml(item) {
        const replacements = item.replacements || [];

        if (!replacements.length) return '';

        const replacementButtonStatus = getReplacementButtonStatus(replacements);

        // Use .filter to get only replacements with at least one order
        const validReplacements = replacements.filter(
            replacement => Array.isArray(replacement.orders) && replacement.orders.length > 0
        );

        if (!validReplacements.length) return '';

        const html = validReplacements.map(createReplacementOrdersLayoutHtml).join('');

        return `
            <div class="shipment__item-replacements">
                <a href="javascript:void(0)" class="shipment__item-link" data-replacement-id="${escapeHtmlAttr(item.id)}" data-title="${escapeHtmlAttr(window.orderTrackerTranslations?.replacementsTitle)}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                        <path d="M4.7 7.5H10.3M7.5 4.7L10.3 7.5L7.5 10.3M14.5 7.5C14.5 11.366 11.366 14.5 7.5 14.5C3.63401 14.5 0.5 11.366 0.5 7.5C0.5 3.63401 3.63401 0.5 7.5 0.5C11.366 0.5 14.5 3.63401 14.5 7.5Z" stroke="#5C5F5C" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span class="small-hide">${escapeHtml(window.orderTrackerTranslations?.replacementsTitle)}</span>
                    <span class="medium-up-hide">${escapeHtml(window.orderTrackerTranslations?.replacementsTitleMobile)}</span>
                </a>
                <div class="shipment__item-replacements__drawer-content" data-replacement-id="${escapeHtmlAttr(item.id)}" hidden>
                    <div class="shipment__item-replacements__drawer-content-inner">
                        <div class="shipment__item-original-product">Original order: ${escapeHtml(item.quantity)}x ${escapeHtml(item.itemDescription)}</div>
                        <div class="shipment__item-replacements-list">
                            ${html}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    function replacementRowsStatusList(replacement) {
        if (!replacement || typeof replacement !== 'object') return [];
        if (Array.isArray(replacement.orders) && replacement.orders.length > 0) {
            const list = [];
            for (const order of replacement.orders) {
                const oi = order.orderDetails?.orderItems;
                if (!oi) continue;
                (oi.shipments || []).forEach(() => list.push('despatch'));
                (oi.preparingForDespatchShipments || []).forEach(() => list.push('in progress'));
                (oi.outstandingBatches || []).forEach(() => list.push('outstanding'));
            }
            return list;
        }
        const st = String(replacement.status || '').trim().toLowerCase();
        return st ? [st] : [];
    }

    function getReplacementButtonStatus(replacements = []) {
        const statuses = replacements.flatMap(r => replacementRowsStatusList(r));

        const isDespatchedStatus = (status) => status === 'despatch';

        const despatchedCount = statuses.filter(isDespatchedStatus).length;
        if (despatchedCount === 0) return window.orderTrackerTranslations?.statusOutstanding;
        if (despatchedCount === statuses.length) return window.orderTrackerTranslations?.statusDespatched;

        return window.orderTrackerTranslations?.statusPartDespatched;
    }

    // Populate order summary in right column
    function populateOrderSummary(data) {
        try {
            const orderSummary = data.orderSummary;
            if (!orderSummary) return;

            // Subtotal
            const orderSubtotal = formatMoney(orderSummary.subtotal);
            populateDataField({
                selector: '#order_subtotal',
                value: orderSubtotal,
                parentElementSelector: '.summary-row--subtotal'
            });
            // Shipping
            const orderShipping = orderSummary.shippingTotal > 0 ? formatMoney(orderSummary.shippingTotal) : (window.orderTrackerTranslations?.summaryFreeShipping);
            populateDataField({
                selector: '#order_shipping',
                value: orderShipping,
                parentElementSelector: '.summary-row--shipping'
            });
            // Discounts
            const orderDiscounts = orderSummary.discountTotal > 0 ? '-' + formatMoney(orderSummary.discountTotal) : 0;
            populateDataField({
                selector: '#order_discounts',
                value: orderDiscounts,
                parentElementSelector: '.summary-row--discounts'
            });

            // Gift Card
            const orderGiftCard = orderSummary.giftCardTotal > 0 ? formatMoney(orderSummary.giftCardTotal) : 0;
            populateDataField({
                selector: '#order_gift_card',
                value: orderGiftCard,
                parentElementSelector: '.summary-row--gift-card'
            });

            // Total
            const orderTotal = formatMoney(orderSummary.transactionTotal);
            populateDataField({
                selector: '#order_total',
                value: orderTotal,
                parentElementSelector: '.summary-row--total'
            });

            // Refunds
            const refundsSection = document.getElementById('refunds_section');
            const refundsTotal = document.getElementById('refunds_total');
            
            if (orderSummary.refundTotals && orderSummary.refundTotals.length > 0) {
                // Calculate total refunded amount
                const totalRefunds = orderSummary.refundTotals.reduce((sum, refund) => sum + Math.abs(refund.amount), 0);
                
                if (refundsTotal) refundsTotal.textContent = formatMoney(totalRefunds);
                if (refundsSection) refundsSection.style.display = 'flex';
            } else {
                if (refundsSection) refundsSection.style.display = 'none';
            }
            
            // Enhanced shipping method display
            const shippingMethodElement = document.getElementById('shipping_method');
            if (orderSummary.shippingMethod) {
                if (shippingMethodElement) {
                    let shippingText = orderSummary.shippingMethod.name || 'Standard Shipping';

                    // Remove the parentheses part from the shipping method name
                    // e.g., "Standard (Get it by 20 – 27 Mar)" becomes "Standard"
                    const nameWithoutParentheses = shippingText.replace(/\s*\([^)]*\).*$/, '');
                    
                    shippingMethodElement.textContent = nameWithoutParentheses;
                }
            } else if (shippingMethodElement) {
                shippingMethodElement.textContent = 'N/a';
            }

        } catch (error) {
            console.error('Error in populateOrderSummary:', error);
        }
    }

    function populatePaymentMethod(orderSummary) {
        try {
            if (!orderSummary) return;
            const paymentMethodElement = document.getElementById('payment_method');
            const paymentMethodRow = document.querySelector('.order-card__item-row--payment-method');

            // Revised logic for updated API: paymentMethod is now an array
            if (Array.isArray(orderSummary.paymentMethod) && paymentMethodElement) {
                const paymentParts = [];
                const seenPaymentLabels = new Set();

                function addPaymentPart(label) {
                    if (!label || seenPaymentLabels.has(label)) return;
                    seenPaymentLabels.add(label);
                    paymentParts.push(label);
                }

                orderSummary.paymentMethod.forEach(method => {
                    // Check for gift card
                    if (
                        method.gateway &&
                        method.gateway.toLowerCase().includes("gift_card")
                    ) {
                        addPaymentPart("Gift Card");
                        return;
                    }

                    // Card payment
                    if (method.cardDetails) {
                        let cardType = method.cardDetails.cardCompany || 'Card';
                        let cardText = cardType;

                        if (method.cardDetails.cardNumber) {
                            // Find last 4 digits
                            const cardNumber = method.cardDetails.cardNumber;
                            const last4Match = cardNumber.match(/(\d{4})$/);
                            if (last4Match) {
                                cardText += ` ending in ${last4Match[1]}`;
                            }
                        }

                        addPaymentPart(cardText);
                        return;
                    }

                    // Gateway fallback
                    if (method.gateway) {
                        let gatewayText = method.gateway.replace(/_/g, " ");
                        addPaymentPart(
                            gatewayText.charAt(0).toUpperCase() + gatewayText.slice(1)
                        );
                    }
                });

                if (paymentParts.length > 0) {
                    paymentMethodElement.textContent = paymentParts.join(', ');
                    paymentMethodRow.classList.remove('hidden');
                } else {
                    paymentMethodRow.classList.add('hidden');
                }
            } else {
                paymentMethodRow.classList.add('hidden');
            }
        } catch (error) {
            console.error('Error in populatePaymentMethod:', error);
        }
    }

    /**
     * Returns the total value of gift cards applied to the order.
     * Expects orderSummary.paymentMethod to be an array, each with gateway containing "gift_card" and amount.
     *
     * @param {Object} orderSummary - The order summary object with paymentMethod array.
     * @returns {number} The total gift card amount applied to the order.
     */
    function getGiftCardTotalUsed(orderSummary) {
        if (
            !orderSummary ||
            !Array.isArray(orderSummary.paymentMethod)
        ) {
            return 0;
        }

        let total = 0;

        orderSummary.paymentMethod.forEach(method => {
            if (
                method.gateway &&
                method.gateway.toLowerCase().includes('gift_card')
            ) {
                if (typeof method.amount === 'number') {
                    total += method.amount;
                } else if (typeof method.amount === 'string' && !isNaN(Number(method.amount))) {
                    total += parseFloat(method.amount);
                }
            }
        });

        return total;
    }

    // Populate customer information in right column
    function populateCustomerInfo(data) {
        try {
            // Contact email
            populateDataField({
                selector: '#contact_email',
                value: data.customerInformation?.email,
                parentElementSelector: '.order-card__item-row--contact-email'
            });

            // Contact phone
            populateDataField({
                selector: '#contact_phone',
                value: data.customerInformation?.phone || data.orderDetails?.shippingAddress?.shippingPhone,
                parentElementSelector: '.order-card__item-row--contact-phone'
            });

            // Payment method
            populatePaymentMethod(data.orderSummary);

            // Shipping address
            populateDataField({
                selector: '#shipping_address',
                value: formatAddress(data.orderDetails?.shippingAddress),
                asHtml: true,
                parentElementSelector: '.order-card__item-row--shipping-address'
            });

            // Billing address
            populateDataField({
                selector: '#billing_address',
                value: formatAddress(data.orderDetails.shippingAddress),
                asHtml: true,
                parentElementSelector: '.order-card__item-row--billing-address'
            });
        } catch (error) {
            console.error('Error in populateCustomerInfo:', error);
        }
    }

    function populateDataField(options = {}) {
        const { selector, value, asHtml = false, parentElementSelector = null } = options;
        const htmlElement = document.querySelector(selector);
        if (!htmlElement) return;

        if (value) {
            if (asHtml) htmlElement.innerHTML = value;
            else htmlElement.textContent = String(value);
        }

        toggleElementVisibility(parentElementSelector, value ? true : false);
    }

    function toggleElementVisibility(selector, show = true) {
        const element = document.querySelector(selector);
        if (!element) return;

        if (element) {
            if (show) element.classList.remove('hidden');
            else element.classList.add('hidden');
        }
    }

    function formatAddress(address) {
        if (!address) return 'N/A';
        
        let formattedAddress = '';
        if (address.address1) formattedAddress += `<span>${escapeHtml(address.address1)}</span>, `;
        if (address.address2) formattedAddress += `<span>${escapeHtml(address.address2)}</span>, `;
        if (address.city) formattedAddress += `<span>${escapeHtml(address.city)}</span>, `;
        if (address.province) formattedAddress += `<span>${escapeHtml(address.province)}</span>, `;
        if (address.state) formattedAddress += `<span>${escapeHtml(address.state)}</span>, `;
        if (address.zipCode) formattedAddress += `<span>${escapeHtml(address.zipCode)}</span>`;

        return formattedAddress || 'N/A';
    }

    function showLoadingScreen() {
        toggleElementVisibility(selectors.loadingMessage, true);
        toggleElementVisibility(selectors.orderDetailsContainer, false);
        toggleElementVisibility(selectors.formContainer, false);
        toggleElementVisibility(selectors.alertContainer, false);
    }

    function showError(message) {
        toggleElementVisibility(selectors.orderDetailsContainer, false);
        toggleElementVisibility(selectors.formContainer, false);
        toggleElementVisibility(selectors.alertContainer, true);
        toggleElementVisibility(selectors.loadingMessage, false);
        
        const alertMessage = document.getElementById('alert_message');
        if (alertMessage) {
            alertMessage.textContent = message;
        }
    }

    function showForm() {
        toggleElementVisibility(selectors.orderDetailsContainer, false);
        toggleElementVisibility(selectors.alertContainer, false);
        toggleElementVisibility(selectors.loadingMessage, false);
        toggleElementVisibility(selectors.formContainer, true);
        cachedOrderData = null;
        window.OrderAmendsFlow?.resetOrderAmendsState?.();
        window.OrderAmendsFlow?.setCachedOrderData?.(null);
        window.OrderAmendsFlow?.updateOrderAmendsActivateCta?.(null);
        window.OrderReturnsFlow?.updateReturnsCtaVisibility?.(null);
        window.OrderReportIssueFlow?.updateReportIssueCtaVisibility?.(null);
        if (window.OrderReportIssueFlow?.isReportIssueFlowActive?.()) {
            window.OrderReportIssueFlow.exitReportIssueMode?.({ clearDraft: false });
        }
        notifyAddressUpdateOrderHidden();

        const orderIdInput = document.getElementById('order_id');
        const emailInput = document.getElementById('billingEmail');
        const trackButton = document.getElementById('trackorder_button');
        
        // Clear URL parameters
        history.pushState({}, '', window.location.pathname);
        
        // Scroll to top smoothly
        window.scrollTo({top: 0, behavior: 'smooth'});

        // Clear form inputs
        if (orderIdInput) orderIdInput.value = '';
        if (emailInput) emailInput.value = '';
        if (trackButton) trackButton.classList.remove('disabled');
    }

    function parseDateFromDMY(value) {
        if (value == null || value === '') return null;
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : value;
        }

        const str = typeof value === 'string' ? value.trim() : String(value).trim();
        if (!str) return null;

        // Support ISO timestamps from API, e.g. "2026-03-30T07:00:00Z".
        // Normalize to UTC calendar day to keep date-only behavior consistent.
        let iso = DateTime.fromISO(str, { setZone: true });
        if (!iso.isValid) {
            iso = DateTime.fromISO(str, { zone: 'utc' });
        }
        if (iso.isValid) {
            return iso.toUTC().startOf('day').toJSDate();
        }

        let dt = DateTime.fromFormat(str, 'dd/MM/yyyy', { zone: 'utc' });
        if (!dt.isValid) {
            dt = DateTime.fromFormat(str, 'd/M/yyyy', { zone: 'utc' });
        }
        if (!dt.isValid) return null;

        const [d, m, y] = str.split('/').map((p) => parseInt(p, 10));
        if (dt.day !== d || dt.month !== m || dt.year !== y) return null;

        return dt.startOf('day').toJSDate();
    }

    function formatDateRange(startDate, endDate) {
        if (!startDate || !endDate) return '';

        const s = utcFromJsDate(startDate);
        const e = utcFromJsDate(endDate);
        if (!s.isValid || !e.isValid) return '';

        if (s.toMillis() === e.toMillis()) {
            return s.toFormat('dd/MM/yyyy');
        }

        const startMonth = FORMAT_DATE_MONTH_NAMES_SHORT[s.month - 1];
        const endMonth = FORMAT_DATE_MONTH_NAMES_SHORT[e.month - 1];

        let dateRange = '';
        const startDay = s.day;
        const endDay = e.day;
        const startYear = s.year;
        const endYear = e.year;
        const currentYear = DateTime.now().setZone(ORDER_TRACKER_BUSINESS_TZ).year;

        if (startYear === endYear && startMonth === endMonth) {
            dateRange = `${startDay}-${endDay} ${startMonth} ${currentYear != startYear ? `${startYear}` : ''}`;
        } else if (startYear === endYear) {
            dateRange = `${startDay} ${startMonth} ${currentYear != startYear ? startYear : ''} - ${endDay} ${endMonth} ${currentYear != endYear ? endYear : ''}`;
        } else {
            dateRange = `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
        }

        return dateRange.trim();
    }

    // Helper function to format shipment dates for display (handles both single dates and date ranges)
    function formatShipmentDateDisplay(shipmentDate) {
        if (shipmentDate == null || shipmentDate === '') return '';

        // Already a formatted date range string from formatDateRange / UI
        if (typeof shipmentDate === 'string' &&
            (shipmentDate.includes(' - ') ||
             /^[0-9]+-[0-9]+\s+[A-Za-z]+/.test(shipmentDate) ||
             /[A-Za-z]+\s+[0-9]+\s*-\s*[A-Za-z]+\s+[0-9]+/.test(shipmentDate))) {
            return shipmentDate;
        }

        if (shipmentDate instanceof Date && !isNaN(shipmentDate.getTime())) {
            return formatDate(shipmentDate, 'j M Y');
        }

        if (typeof shipmentDate === 'string') {
            const parsed = parseDateFromDMY(shipmentDate);
            if (parsed && !isNaN(parsed.getTime())) {
                return formatDate(parsed, 'j M Y');
            }
        }

        return typeof shipmentDate === 'string' ? shipmentDate : '';
    }

    function getOriginalDeliveryDates(orderData) {
        const orderItems = orderData.orderDetails?.orderItems;

        const earliestDates = [];
        const latestDates = [];
        let hasRevisedDeliveryDates = false;

        const collectFromShipmentsOrBatches = (shipmentsOrBatches) => {
            if (!shipmentsOrBatches) return;

            shipmentsOrBatches.forEach(data => {
                if (data.hasOriginalDeliveryDates) { 
                    earliestDates.push(data.earliestDeliveryDate);
                    latestDates.push(data.latestDeliveryDate);
                }

                // any of the shipments or batches contains revised dates
                if (data.hasRevisedDeliveryDates) {
                    hasRevisedDeliveryDates = true;
                }
            });
        };

        [
            orderItems.shipments,
            orderItems.preparingForDespatchShipments,
            orderItems.outstandingBatches,
        ].forEach(collectFromShipmentsOrBatches);

        const earliest = earliestDates.length > 0 
            ? new Date(Math.min(...earliestDates.map(date => date.getTime())))
            : null;

        const latest = latestDates.length > 0 
            ? new Date(Math.max(...latestDates.map(date => date.getTime())))
            : null;

        return { earliest, latest, hasRevisedDeliveryDates };
    }

    function getOrderState(data) {
        const orderItems = data.orderDetails.orderItems;
        const hasShipped = orderItems.shipments && orderItems.shipments.length > 0;
        const hasPreparing = orderItems.preparingForDespatchShipments && orderItems.preparingForDespatchShipments.length > 0;
        const hasOutstanding = orderItems.outstandingBatches && orderItems.outstandingBatches.length > 0;
        const hasCancelled = orderItems.cancelledItems && orderItems.cancelledItems.length > 0;
        // Only treat as "all cancelled" when there are cancelled items and no other active items
        const hasOnlyCancelled = hasCancelled && !hasShipped && !hasPreparing && !hasOutstanding;

        return {
            hasShipped,
            hasPreparing,
            hasOutstanding,
            hasCancelled,
            hasOnlyCancelled,
            hasOnlyOutstanding: hasOutstanding && !hasShipped && !hasPreparing,
            hasProgressed: hasShipped || hasPreparing
        };
    }

    function isDatePastThreshold(targetDate, thresholdDays = 10) {
        if (!targetDate || isNaN(targetDate.getTime())) return false;

        const ukTodayKey = getUKBusinessCalendarKey(new Date());
        const targetKey = getUTCCalendarKey(targetDate);
        const diffDays = calendarKeysDiffDays(ukTodayKey, targetKey);

        return diffDays > thresholdDays;
    }

    function getDeliveryTimelineEntries(orderItems, orderState) {
        const entries = [];
        if (!orderItems) return entries;

        if (orderState.hasOnlyCancelled) {
            entries.push({
                label: window.orderTrackerTranslations?.deliveryCancelled,
                dateRange: '',
                isCompleted: true
            });
        } else {
            // Shipments + preparing + outstanding: same date windows merged, then one or two timeline rows
            const upcomingGroups = buildUpcomingTimelineGroups(orderItems, orderState);

            if (upcomingGroups.length > 0) {
                pushUpcomingDeliveryTimelineEntries(entries, upcomingGroups);
            } else {
                entries.push({
                    label: window.orderTrackerTranslations?.deliveryFullyDespatched,
                    dateRange: '',
                    isCompleted: true
                });
            }
        }

        return entries;
    }

    function getDeliveryTimelineDateHtml(label, date, options = {}) {
        const { isCompleted = false, delayMessage = null } = options;
        
        let cssClasses = 'order-header__delivery-date';

        if (isCompleted) {
            cssClasses += ' order-header__delivery-date--completed';
        }
        
        return `
            <div class="${cssClasses}">
                <p class="order-header__delivery-window-text order-header__delivery-window-title">
                    ${escapeHtml(label)}
                </p>
                ${
                    date
                        ? `
                            <p class="order-header__delivery-window-text order-header__delivery-window-date">
                                <strong>${escapeHtml(date)}</strong>
                                ${
                                    delayMessage
                                        ? ` <span class="order-header__delivery-window-delay-message">(${escapeHtml(delayMessage)})</span>`
                                        : ''
                                }
                            </p>
                        `
                        : ''
                }
            </div>
        `;
    }

    function getDelayInformation(group) {
        if (!group.hasRevisedDeliveryDates) {
            return { delayMessage: null };
        }

        const descriptions = group.delayDescriptions || [];
        const firstDescription = descriptions.length > 0 ? descriptions[0] : null;
        const delayMessage = firstDescription
            ? window.deliveryReasonTranslations?.[firstDescription] || null
            : null;

        return { delayMessage };
    }

    function getDateRangeKey(earliestDate, latestDate) {
        if (!earliestDate || !latestDate) return null;
        const e = earliestDate.getTime();
        const l = latestDate.getTime();

        return `${e}|${l}`;
    }

    function sortGroupsByEarliestDate(groups) {
        groups.sort((a, b) => {
            if (!a.earliestDate) return 1;
            if (!b.earliestDate) return -1;
            return a.earliestDate - b.earliestDate;
        });
        return groups;
    }

    function mergeTimelineClusterMembers(a, b) {
        const delayDescriptions = [...(a.delayDescriptions || [])];
        (b.delayDescriptions || []).forEach(dd => {
            if (dd && !delayDescriptions.includes(dd)) delayDescriptions.push(dd);
        });

        return {
            earliestDate: new Date(Math.min(a.earliestDate.getTime(), b.earliestDate.getTime())),
            latestDate: new Date(Math.max(a.latestDate.getTime(), b.latestDate.getTime())),
            deliveryCount: (a.deliveryCount || 0) + (b.deliveryCount || 0),
            totalProducts: (a.totalProducts || 0) + (b.totalProducts || 0),
            hasRevisedDeliveryDates: !!(a.hasRevisedDeliveryDates || b.hasRevisedDeliveryDates),
            delayDescriptions,
        };
    }

    function cloneTimelineGroupForAggregation(g) {
        return {
            earliestDate: g.earliestDate,
            latestDate: g.latestDate,
            deliveryCount: g.deliveryCount || 0,
            totalProducts: g.totalProducts || 0,
            hasRevisedDeliveryDates: !!g.hasRevisedDeliveryDates,
            delayDescriptions: [...(g.delayDescriptions || [])],
        };
    }

    function aggregateTimelineGroups(groups) {
        if (!groups.length) return null;
        return groups.slice(1).reduce(
            (acc, g) => mergeTimelineClusterMembers(acc, g),
            cloneTimelineGroupForAggregation(groups[0])
        );
    }

    function timelineGroupFromShipmentOrBatch(shipmentOrBatch) {
        const { earliestDate, latestDate } = getEarliestAndLatestDate(shipmentOrBatch);
        if (!earliestDate || !latestDate) return null;

        const lineItems = shipmentOrBatch.items || [];
        const dd = shipmentOrBatch.delayedChangeReasonDescription;
        const delayDescriptions = dd ? [String(dd).trim()].filter(Boolean) : [];

        return {
            earliestDate,
            latestDate,
            deliveryCount: 1,
            totalProducts: calculateTotalProducts(lineItems),
            hasRevisedDeliveryDates: !!shipmentOrBatch.hasRevisedDeliveryDates,
            delayDescriptions,
        };
    }

    function individualTimelineGroupsFromList(list) {
        if (!list?.length) return [];

        return list.map(s => timelineGroupFromShipmentOrBatch(s)).filter(Boolean);
    }

    function clusterByEarliestProximity(rows, thresholdDays) {
        const valid = rows.filter(r => r.earliestDate && r.latestDate);

        if (!valid.length) return [];

        valid.sort((a, b) => {
            const te = a.earliestDate - b.earliestDate;
            if (te !== 0) return te;
            return a.latestDate - b.latestDate;
        });

        const clusters = [];
        let current = cloneTimelineGroupForAggregation(valid[0]);
        let anchor = valid[0].earliestDate;

        for (let i = 1; i < valid.length; i++) {
            const row = valid[i];

            // compare the anchor to the new row's earliest date
            if (calendarDaysApart(anchor, row.earliestDate) <= thresholdDays) {
                current = mergeTimelineClusterMembers(current, row);
            } else {
                clusters.push(current);
                current = cloneTimelineGroupForAggregation(row);

                // change the anchor to the new row's earliest date
                anchor = row.earliestDate;
            }
        }

        clusters.push(current);

        return clusters;
    }

    function buildUpcomingTimelineGroups(orderItems, orderState) {
        const { proximityDays } = getOrderTrackerTimelineConfig();
        const raw = [];

        raw.push(
            ...individualTimelineGroupsFromList(
                [
                    ...(orderState.hasShipped ? orderItems.shipments : []),
                    ...(orderState.hasPreparing ? orderItems.preparingForDespatchShipments : []),
                    ...(orderState.hasOutstanding ? orderItems.outstandingBatches : [])
                ]
            )
        );

        const clustered = clusterByEarliestProximity(raw, proximityDays);

        return clustered.filter(
            g => g.earliestDate && g.latestDate && !isDatePastThreshold(g.latestDate, 10)
        );
    }

    function formatTimelineQuantityPhrase(group) {
        const dMany = window.orderTrackerTranslations?.deliveryDeliveries;
        const pOne = window.orderTrackerTranslations?.deliveryProduct;
        const pMany = window.orderTrackerTranslations?.deliveryProducts;

        const deliveries = group.deliveryCount || 0;
        if (deliveries > 1) {
            return `${deliveries} ${dMany}`;
        }

        const totalProducts = group.totalProducts || 0;
        return `${totalProducts} ${totalProducts === 1 ? pOne : pMany}`;
    }

    function formatPrimaryTimelineLabel(baseLabel, group) {
        const phrase = formatTimelineQuantityPhrase(group);
        return `${baseLabel} (${phrase})`;
    }

    function pushUpcomingDeliveryTimelineEntries(entries, upcomingGroups) {
        const { maxExplicitGroups } = getOrderTrackerTimelineConfig();
        const n = upcomingGroups.length;

        if (n === 1) {
            const g = upcomingGroups[0];
            const baseLabel = window.orderTrackerTranslations?.deliveryEstDelivery;
            const delayInfo = getDelayInformation(g);

            entries.push({
                label: formatPrimaryTimelineLabel(baseLabel, g),
                dateRange: formatDateRange(g.earliestDate, g.latestDate),
                delayMessage: delayInfo.delayMessage,
            });

            return;
        }

        const k = Math.min(maxExplicitGroups, n);
        const followingLabel = window.orderTrackerTranslations?.deliveryEstNextDelivery;
        const nextLabel = window.orderTrackerTranslations?.deliveryEstNextDelivery;

        for (let i = 0; i < k; i++) {
            const g = upcomingGroups[i];
            const baseLabel = i === 0 ? nextLabel : followingLabel;
            const delayInfo = getDelayInformation(g);

            entries.push({
                label: formatPrimaryTimelineLabel(baseLabel, g),
                dateRange: formatDateRange(g.earliestDate, g.latestDate),
                delayMessage: delayInfo.delayMessage,
            });
        }

        if (k < n) {
            const tail = upcomingGroups.slice(k);
            const agg = aggregateTimelineGroups(tail);
            if (!agg) return;
            const remDelayInfo = getDelayInformation(agg);

            const remPhrase = formatTimelineQuantityPhrase(agg);
            const remLabel = `${window.orderTrackerTranslations?.deliveryEstRemaining} (${remPhrase})`;

            entries.push({
                label: remLabel,
                dateRange: formatDateRange(agg.earliestDate, agg.latestDate),
                delayMessage: remDelayInfo.delayMessage,
            });
        }
    }

    function getReturnsTotalQuantity(returns = []) {
        const fromReturn = window.OrderReturnsFlow?.getReturnedQuantityFromReturn;
        if (typeof fromReturn === 'function') {
            return (returns || []).reduce((total, ret) => total + fromReturn(ret), 0);
        }
        return (returns || []).reduce((total, ret) => {
            const qty = Number(ret.parentItemQuantity);
            return Number.isFinite(qty) ? total + qty : total;
        }, 0);
    }

    function getShipmentOrBatchGroupsByDate(shipments) {
        if (!shipments || shipments.length === 0) return [];

        const dateGroups = new Map();

        shipments.forEach(shipment => {
            const { earliestDate, latestDate } = getEarliestAndLatestDate(shipment);
            if (!earliestDate || !latestDate) return;

            const dateKey = getDateRangeKey(earliestDate, latestDate);
            if (!dateKey) return;

            if (!dateGroups.has(dateKey)) {
                dateGroups.set(dateKey, {
                    earliestDate,
                    latestDate,
                    shipmentRows: [],
                    hasRevisedDeliveryDates: false,
                    delayDescriptions: [],
                });
            }

            const g = dateGroups.get(dateKey);
            g.shipmentRows.push(shipment);

            if (shipment.hasRevisedDeliveryDates) {
                g.hasRevisedDeliveryDates = true;
            }
            const dd = shipment.delayedChangeReasonDescription;
            if (dd && !g.delayDescriptions.includes(dd)) g.delayDescriptions.push(dd);
        });

        const groups = [];

        dateGroups.forEach(group => {
            const lineItems = group.shipmentRows.flatMap(s => s.items || []);
            groups.push({
                earliestDate: group.earliestDate,
                latestDate: group.latestDate,
                shipmentRows: group.shipmentRows,
                items: lineItems,
                deliveryCount: group.shipmentRows.length,
                totalProducts: calculateTotalProducts(lineItems),
                hasRevisedDeliveryDates: group.hasRevisedDeliveryDates,
                delayDescriptions: group.delayDescriptions,
            });
        });

        return sortGroupsByEarliestDate(groups);
    }

    function getEarliestAndLatestDate(group) {
        let earliestDate = group.earliestDeliveryDate;
        let latestDate = group.latestDeliveryDate;

        if (group.hasRevisedDeliveryDates) {
            earliestDate = group.revisedEarliestDeliveryDate;
            latestDate = group.revisedLatestDeliveryDate;
        }

        return { earliestDate, latestDate };
    }

    function calculateTotalProducts(items) {
        if (!items || items.length === 0) return 0;

        let totalQuantity = 0;
        const processedSetItems = new Set();

        items.forEach(item => {
            if (item.setItem && item.setItem.trim() !== '') {
                // If item has setItem value, only count unique setItems
                if (!processedSetItems.has(item.setItem)) {
                    processedSetItems.add(item.setItem);
                    const setQuantity = parseInt(item.setQuantity, 10);
                    totalQuantity += isNaN(setQuantity) ? 1 : setQuantity;
                }
            } else {
                // If setItem is empty, use the item's quantity
                const quantity = parseInt(item.quantity, 10);
                totalQuantity += isNaN(quantity) ? 1 : quantity;
            }
        });

        return totalQuantity;
    }

    function formatDate(date, format, hideCurrentYear = true) {
        if (date == null || date === '') return '';

        let dt;
        if (typeof date === 'string') {
            const t = date.trim();
            dt = DateTime.fromISO(t, { setZone: true });
            if (!dt.isValid) dt = DateTime.fromISO(t, { zone: 'utc' });
            if (!dt.isValid) dt = DateTime.fromFormat(t, 'dd/MM/yyyy', { zone: 'utc' });
            if (!dt.isValid) dt = DateTime.fromFormat(t, 'd/M/yyyy', { zone: 'utc' });
            if (dt.isValid) dt = dt.toUTC();
        } else if (date instanceof Date) {
            dt = utcFromJsDate(date);
        } else {
            return '';
        }

        if (!dt.isValid) return '';

        const day = dt.day;
        const month = dt.month - 1;
        const year = dt.year;
        const currentYear = DateTime.now().setZone(ORDER_TRACKER_BUSINESS_TZ).year;

        // Determine if year should be shown
        const shouldShowYear = !hideCurrentYear || (year !== currentYear);
        
        // Month names: storefront locale via window.orderTrackerTranslations (see order-status-js-translations.liquid)
        const monthFull = FORMAT_DATE_MONTH_NAMES[month];
        const monthShort = FORMAT_DATE_MONTH_NAMES_SHORT[month];

        // Format mapping
        const formatMap = {
            // Day
            'd': day.toString().padStart(2, '0'),           // 01-31
            'j': day.toString(),                            // 1-31
            
            // Month
            'm': (month + 1).toString().padStart(2, '0'),   // 01-12
            'n': (month + 1).toString(),                    // 1-12
            'F': monthFull,
            'M': monthShort,
            
            // Year (conditionally shown)
            'Y': shouldShowYear ? year.toString() : '',     // 2024 or empty
            'y': shouldShowYear ? year.toString().slice(-2) : '', // 24 or empty
            
            // Common separators (literal)
            '/': '/',
            '-': '-',
            ' ': ' ',
            ',': ',',
            '.': '.'
        };
        
        // Replace format tokens
        let result = format.replace(/[djmnFMYy\/\-\s,.]/g, match => {
            const replacement = formatMap[match];
            return replacement !== undefined ? replacement : match;
        });
        
        // Clean up extra spaces or separators when year is hidden
        if (hideCurrentYear && year === currentYear) {
            result = result
                .replace(/,\s*$/, '')           // Remove trailing comma and space
                .replace(/\s+/g, ' ')          // Normalize multiple spaces
                .trim();                       // Remove leading/trailing spaces
        }

        return result;
    }

    // Function to copy order number to desktop location for responsive design
    function copyOrderNumberToDesktop() {
        const orderNumberElement = document.querySelector('.js-order-number-display');
        const desktopContainer = document.querySelector('.order-header__info-order-number');
        
        if (desktopContainer && orderNumberElement) {
            desktopContainer.querySelector('.order_number')?.remove();

            // Clone the order number element
            const clonedElement = orderNumberElement.cloneNode(true);
            clonedElement.className = 'order_number';
            
            // Append to desktop container
            desktopContainer.prepend(clonedElement);
        }
    }

    function renderDownloadInvoiceButton(orderId) {
        // make sure the button is hidden first
        const invoiceBtn = document.getElementById('download_invoice_btn');
        if (!invoiceBtn) return;
        invoiceBtn.href = '';
        invoiceBtn.classList.add('hidden');

        const simpleInvoiceEnabled = document.getElementById('order-tracking').dataset.simpleInvoiceEnabled;
        const simpleInvoiceStoreId = document.getElementById('order-tracking').dataset.simpleInvoiceStoreId;
        const simpleInvoiceInvoiceKey = document.getElementById('order-tracking').dataset.simpleInvoiceInvoiceKey;

        if (!simpleInvoiceEnabled || !simpleInvoiceStoreId || !simpleInvoiceInvoiceKey) return;
        if (! orderId) return;

        const shop = window.Shopify?.shop;
        if (! shop) return;

        const hash = `${md5(`${orderId}${simpleInvoiceStoreId}${orderId}`)}${simpleInvoiceInvoiceKey}${orderId}`;
        invoiceBtn.href = `https://${shop}/apps/download?hash=${encodeURIComponent(hash)}&ids=${encodeURIComponent(orderId)}&template_type=invoice`;
        invoiceBtn.classList.remove('hidden');
    }
})();